import { useCallback, useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import routes from '~/routes';
import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { Button } from '@carbon/react';
import { Location, LocationFilled } from '@carbon/react/icons';
import {
  getFilterVariant,
  type ColumnType,
  type DataTableColumnDef,
  type DataTableColumnMeta,
  type DataTableConfig,
} from 'components/data-table';
import { EMPTY_CELL, LinkCell } from 'components/link-cell';
import type { Expression } from 'mapbox-gl';
import type { SubtractNRI, TractNRI } from 'components/nri-table/types';
import type { BBox } from 'components/query-map/query-map';
import type { AggregationPeriod } from 'types/misc';
import { camelCase, groupBy, isEqual, partition, startCase } from 'lodash-es';
import moment from 'moment';
import { caseIncompleteText } from 'lib/evaluation-case/readiness-text';
import {
  isShapeCarryingDomainKey,
  SHAPE_CARRYING_DOMAIN_KEYS,
} from 'lib/evaluation-case/scenario-domains';
import { formatNumber, formatRange } from 'utils/number';
import { parseMomentUtc } from 'utils/dates';
import { wellStatusLabel } from 'utils/well-status';
import {
  GENERIC_CHART_ELEMENTS,
  type ExportImageOptions,
} from 'components/chart-export';
import type { ChartExportGroup } from './chart-export-registry';
import { SPATIAL_EXPORT_LAYERS } from './spatial-export-utils';
import {
  locatedRowAtom,
  masterDataVisibleColumnsAtom,
  type ChildHighlight,
  type MapHighlight,
} from './atoms';
import type {
  AggregationKey,
  AssetAllocationChildResponse,
  AssetRow,
  AssetRowResponse,
  AssetSubtractRow,
  CalculateRequest,
  CaseDomain,
  CashflowPeriod,
  ColorElements,
  ColorGroup,
  ColorRule,
  ColumnPreset,
  ContentExportPayload,
  ContentExportSection,
  DatasetName,
  DepthRangeResponse,
  DisplaySettings,
  EvaluationCaseCoverage,
  EvaluationCaseOverrides,
  RunScenarioRow,
  RunScenariosResponse,
  FilterCatalog,
  FilterKind,
  FilterOption,
  FilterRange,
  FilterSelection,
  InterestItem,
  InventoryPackageOverride,
  InventoryPackageOverrideWire,
  MapElements,
  MapScope,
  MasterDataResponse,
  MasterDataSort,
  MasterDataView,
  OverrideFields,
  PackageMapSource,
  RollupBin,
  RollupCellResponse,
  RollupOptions,
  RollupOptionsResponse,
  RollupPage,
  RollupRow,
  RollupSort,
  ServerFilter,
  SlotAllocationRow,
  SlotAllocationRowResponse,
  SlotDisplayPatch,
  SlotOverrideLogEntry,
  SlotOverrideOperation,
  SlotOverrideWire,
  SlotRow,
  SlotRowResponse,
  SpatialExportConfig,
  SubtractChildResponse,
  TableFilter,
  TractChildResponse,
  TractNriRowResponse,
  TypeCurveOverride,
  TypeCurveOverrideWire,
  ValueRollupRequest,
  ValueRollupResponse,
  VisualSummaryRequest,
  WarehouseColumnFilter,
  WorkingConfig,
} from './types';
import type {
  AggregatedFreeCashFlow,
  AggregatedProductionVolumes,
  FreeCashFlowEntry,
  ProductionType,
  ProductionVolumeEntry,
  SlotCategory,
} from 'types/evaluation';
import type { AggregatedFreeCashFlowResultResponse } from 'types/api-responses';

/** Empty-cell indicator, matching DataTable's `meta.type` formatter default. */
export const EMPTY = EMPTY_CELL;

/**
 * The eval3-new nested-route URLs, built in ONE place so the segment shape lives here
 * (its matcher patterns live in `routes.js`). Every navigation goes through these.
 * INS-11484: the new page now serves the canonical `/eval3` namespace (legacy is `/eval3-legacy`).
 *   - `eval3Base`       → `/packages/:id/eval3` (bare / package landing)
 *   - `eval3WorkspacePath` → `…/workspaces/:workspaceId` (management / browse view)
 *   - `eval3EvalPath`   → `…/workspaces/:workspaceId/eval/:evalId` (an eval; `'new'` = draft)
 *
 * @param {number} packageId - The package the route is scoped to.
 * @return {string} The path.
 */
export const eval3Base = (packageId: number): string =>
  `/packages/${packageId}/eval3`;

/**
 * The package edit (wizard) route — the top-level `/packages/:id/edit` page (matcher in
 * `routes.js`, rendered by `PackageWizardPage`), NOT an eval3-new nested route. Navigated
 * to (same-tab, client-side via history.push) from the left-pane header's edit-package
 * affordance (INS-11395). Derived from the shared `routes.editPackagePath` template (the
 * same one the `<Route>` matcher uses) so the concrete path can't drift from the matcher.
 *
 * @param {number} packageId - The package to edit.
 * @return {string} The `/packages/:id/edit` path.
 */
export const editPackagePath = (packageId: number): string =>
  generatePath(routes.editPackagePath, { id: packageId });

/**
 * The workspace management/browse route.
 *
 * @param {number} packageId - The package.
 * @param {number} workspaceId - The open workspace folder.
 * @return {string} The `…/workspaces/:workspaceId` path.
 */
export const eval3WorkspacePath = (
  packageId: number,
  workspaceId: number
): string => `${eval3Base(packageId)}/workspaces/${workspaceId}`;

/**
 * The eval route (an eval nested under its workspace). `evalId` is a calc id, or the
 * `'new'` sentinel for a from-scratch draft.
 *
 * @param {number} packageId - The package.
 * @param {number} workspaceId - The eval's owning workspace.
 * @param {number | 'new'} evalId - The calc id, or `'new'` for a draft.
 * @return {string} The `…/workspaces/:workspaceId/eval/:evalId` path.
 */
export const eval3EvalPath = (
  packageId: number,
  workspaceId: number,
  evalId: number | 'new'
): string => `${eval3WorkspacePath(packageId, workspaceId)}/eval/${evalId}`;

/**
 * The Saved Evaluations browse route (the pinned folder's center view) — package-scoped, no
 * workspace/eval segment.
 *
 * @param {number} packageId - The package.
 * @return {string} The `…/eval3/saved-evals` path.
 */
export const eval3SavedEvalsPath = (packageId: number): string =>
  `${eval3Base(packageId)}/saved-evals`;

/**
 * The display name for an eval. The eval's "name" IS its `description`; when that's
 * blank we fall back to `<id>-<created-by>`. Shared so the tree leaf, the management
 * table, and the center title bar render the SAME name for the same eval.
 *
 * @param {number} id - The eval (calculation) id.
 * @param {string | null | undefined} description - The eval's description (its name), if any.
 * @param {string | null | undefined} createdBy - The eval author's name (for the fallback).
 * @return {string} The description, or the `<id>-<createdBy>` fallback when it's blank.
 */
export function evalDisplayName(
  id: number,
  description: string | null | undefined,
  createdBy: string | null | undefined
): string {
  if (description) {
    return description;
  }
  return createdBy ? `${id}-${createdBy}` : String(id);
}

/**
 * Calc-mode wire field for eval3 request bodies. `calculationId` is kept in
 * query keys (normalized to `null`) but must be omitted from the request body
 * when falsy — a `null` on the wire would mint a distinct cache entry / trip the
 * BE. Spread the result into a payload so the field appears only in calc mode.
 *
 * @param {number | null | undefined} calculationId - The active calculation id,
 *   or `null`/`undefined` in package mode.
 * @return {{ calculationId: number } | Record<string, never>} `{ calculationId }`
 *   when set, otherwise an empty object.
 */
export function calcModeField(
  calculationId: number | null | undefined
): { calculationId: number } | Record<string, never> {
  return calculationId ? { calculationId } : {};
}

/**
 * The `typeCurveScenarioId` half of a request body, omitted when nothing is picked.
 *
 * Omitted rather than sent as `null` so the BE applies its OWN default (Legacy) in one place —
 * `Query::Packages::InsightsLTDMerge`. Sending null would also work, but then every surface
 * would be asserting a default it does not own, which is how the table and the calculation came
 * to disagree in the implementation this replaces (INS-10669).
 *
 * @param {number | null | undefined} typeCurveScenarioId - The picked scenario, or null.
 * @return {object} `{ typeCurveScenarioId }` when picked, else `{}`.
 */
/** The override log's `type_curve` entry type — the one place the wire value is named. */
export const TYPE_CURVE_OVERRIDE_TYPE = 'type_curve' as const;

/**
 * The Type Curve choices held in an ordered override log, folded to one per (area, formation).
 *
 * Mirrors `Query::Packages::TypeCurveOverrides.effective` — last write wins per key, different keys
 * independent — because the modal seeds from the same log the run resolves with, and two different
 * folds would mean the modal showing something the run will not do.
 *
 * Reads BOTH spellings. Entries committed to a calculation are snake_case (the BE normalizer rewrote
 * them at persist) while entries added this session are camelCase, and both arrive in one array.
 *
 * @param {unknown[]} log - The ordered override log (committed entries then pending ones).
 * @return {TypeCurveOverride[]} One entry per overridden (area, formation), in key order.
 */
export function typeCurveOverridesFromLog(
  log: readonly unknown[] | null | undefined
): TypeCurveOverride[] {
  const folded = new Map<string, TypeCurveOverride>();
  (log ?? []).forEach((raw) => {
    if (typeof raw !== 'object' || raw === null) {
      return;
    }
    const entry = raw as Record<string, unknown>;
    if (String(entry.type) !== TYPE_CURVE_OVERRIDE_TYPE) {
      return;
    }
    const areaId = Number(entry.type_curve_area_id ?? entry.typeCurveAreaId);
    const formation = String(entry.formation ?? '');
    if (!Number.isFinite(areaId) || !formation) {
      return;
    }
    // NOT `??`: a camelCase tombstone carries null on the camel key with the snake key
    // absent, and `null ?? x` would move past the null — then Number(null) === 0 would
    // store header id 0 instead of deleting. Take whichever key EXISTS, null included.
    const rawHeader =
      'type_curve_header_details_id' in entry
        ? entry.type_curve_header_details_id
        : entry.typeCurveHeaderDetailsId;
    const headerId = rawHeader === null ? NaN : Number(rawHeader);
    // A null/absent header is a TOMBSTONE — "back to the scenario default" — and DELETES
    // the key, mirroring the BE fold (TypeCurveOverrides.effective) exactly. Skipping it
    // instead (the previous behaviour) made a revert invisible client-side: the fold kept
    // quoting the old override, so nothing repainted and divergence never registered
    // (INS-10669 tombstones).
    if (!Number.isFinite(headerId)) {
      folded.delete(`${areaId}:${formation}`);
      return;
    }
    folded.set(`${areaId}:${formation}`, {
      typeCurveAreaId: areaId,
      typeCurveAreaName: String(
        entry.type_curve_area_name ?? entry.typeCurveAreaName ?? ''
      ),
      formation,
      typeCurveHeaderDetailsId: headerId,
    });
  });
  return [...folded.values()];
}

/**
 * The wire form of one Type Curve choice — snake_case, matching what the BE normalizer persists, so
 * a committed entry and a freshly-added one read identically on reopen.
 *
 * @param {TypeCurveOverride} override - The choice.
 * @return {TypeCurveOverrideWire} The log entry.
 */
export function toTypeCurveOverrideWire(
  override: TypeCurveOverride
): TypeCurveOverrideWire {
  return {
    type: TYPE_CURVE_OVERRIDE_TYPE,
    type_curve_area_id: override.typeCurveAreaId,
    type_curve_area_name: override.typeCurveAreaName,
    formation: override.formation,
    type_curve_header_details_id: override.typeCurveHeaderDetailsId,
  };
}

/**
 * Whether a log entry is a Type Curve choice — used to strip the old ones before appending the new
 * set, which is how a reverted row's entry is REMOVED rather than left as a no-op marker.
 *
 * A type PREDICATE, not a plain boolean: callers narrow on it, which is what lets
 * `toSlotOverridesWire` pass the entry through without a cast.
 *
 * @param {unknown} entry - A log entry.
 * @return {boolean} True when it is a `type_curve` entry.
 */
export function isTypeCurveOverrideEntry(
  entry: unknown
): entry is TypeCurveOverrideWire {
  if (typeof entry !== 'object' || entry === null) {
    return false;
  }
  return (
    String((entry as Record<string, unknown>).type) === TYPE_CURVE_OVERRIDE_TYPE
  );
}

/**
 * `{ [areaId, formation] => headerId }` as the compact JSON a tile-source URL carries.
 *
 * Mirrors `Query::Packages::TypeCurveOverrides.encode`, including the sort: the BE hashes the same
 * canonical form into the filterSet token, so a different order here would shift the token when
 * nothing about the choices had.
 *
 * @param {TypeCurveOverride[]} overrides - The choices.
 * @return {string | undefined} The encoded param, or undefined when there is nothing to send.
 */
export function encodeTypeCurveOverrides(
  overrides: readonly TypeCurveOverride[]
): string | undefined {
  if (overrides.length === 0) {
    return undefined;
  }
  const triples = overrides
    .map((o) => [o.typeCurveAreaId, o.formation, o.typeCurveHeaderDetailsId])
    .sort((a, b) => {
      const byArea = Number(a[0]) - Number(b[0]);
      return byArea !== 0 ? byArea : String(a[1]).localeCompare(String(b[1]));
    });
  return JSON.stringify(triples);
}

/** The override log's `inventory_package` entry type — the one place the wire value is named. */
export const INVENTORY_PACKAGE_OVERRIDE_TYPE = 'inventory_package' as const;

/**
 * The inventory-package choices held in an ordered override log, folded to one per
 * (area, formation) — the spacing mirror of `typeCurveOverridesFromLog` (INS-10669).
 *
 * Mirrors `Query::Packages::InventoryPackageOverrides.effective`: last write wins per key,
 * a package-less entry is a TOMBSTONE and deletes the key, and BOTH spellings read
 * (committed entries are snake_case, this session's are camelCase, one array).
 *
 * @param {unknown[]} log - The ordered override log (committed entries then pending ones).
 * @return {InventoryPackageOverride[]} One entry per overridden (area, formation), in key order.
 */
export function inventoryPackageOverridesFromLog(
  log: readonly unknown[] | null | undefined
): InventoryPackageOverride[] {
  const folded = new Map<string, InventoryPackageOverride>();
  (log ?? []).forEach((raw) => {
    if (typeof raw !== 'object' || raw === null) {
      return;
    }
    const entry = raw as Record<string, unknown>;
    if (String(entry.type) !== INVENTORY_PACKAGE_OVERRIDE_TYPE) {
      return;
    }
    const areaId = Number(entry.type_curve_area_id ?? entry.typeCurveAreaId);
    const formation = String(entry.formation ?? '');
    if (!Number.isFinite(areaId) || !formation) {
      return;
    }
    // Take whichever key EXISTS, null included — `??` would step past a camelCase
    // tombstone's null onto the absent snake key (see typeCurveOverridesFromLog).
    const rawName =
      'inventory_package_name' in entry
        ? entry.inventory_package_name
        : entry.inventoryPackageName;
    const packageName =
      rawName === null || rawName === undefined ? null : String(rawName);
    if (!packageName) {
      folded.delete(`${areaId}:${formation}`);
      return;
    }
    const rawSpacing = entry.well_spacing_ft ?? entry.wellSpacingFt;
    folded.set(`${areaId}:${formation}`, {
      typeCurveAreaId: areaId,
      typeCurveAreaName: String(
        entry.type_curve_area_name ?? entry.typeCurveAreaName ?? ''
      ),
      formation,
      inventoryPackageName: packageName,
      wellSpacingFt: Number.isFinite(Number(rawSpacing))
        ? Number(rawSpacing)
        : null,
    });
  });
  return [...folded.values()];
}

/**
 * The wire form of one inventory-package choice — snake_case, matching what the BE
 * normalizer persists, so a committed entry and a freshly-added one read identically.
 *
 * @param {InventoryPackageOverride} override - The choice.
 * @return {InventoryPackageOverrideWire} The log entry.
 */
export function toInventoryPackageOverrideWire(
  override: InventoryPackageOverride
): InventoryPackageOverrideWire {
  return {
    type: INVENTORY_PACKAGE_OVERRIDE_TYPE,
    type_curve_area_id: override.typeCurveAreaId,
    type_curve_area_name: override.typeCurveAreaName,
    formation: override.formation,
    inventory_package_name: override.inventoryPackageName,
    well_spacing_ft: override.wellSpacingFt,
  };
}

/**
 * Whether a log entry is an inventory-package choice — the type PREDICATE that lets
 * `toSlotOverridesWire` pass the entry through without a cast (see its TC twin).
 *
 * @param {unknown} entry - A log entry.
 * @return {boolean} True when it is an `inventory_package` entry.
 */
export function isInventoryPackageOverrideEntry(
  entry: unknown
): entry is InventoryPackageOverrideWire {
  if (typeof entry !== 'object' || entry === null) {
    return false;
  }
  return (
    String((entry as Record<string, unknown>).type) ===
    INVENTORY_PACKAGE_OVERRIDE_TYPE
  );
}

/**
 * The package choices as the compact JSON a cache key carries — the spacing mirror of
 * `encodeTypeCurveOverrides`, sorted the same way `InventoryPackageOverrides.canonical`
 * sorts so equal choice sets always encode identically.
 *
 * @param {InventoryPackageOverride[]} overrides - The choices.
 * @return {string | undefined} The encoded param, or undefined when there is nothing to send.
 */
export function encodeInventoryPackageOverrides(
  overrides: readonly InventoryPackageOverride[]
): string | undefined {
  if (overrides.length === 0) {
    return undefined;
  }
  const rows = overrides
    .map((o) => [
      o.typeCurveAreaId,
      o.formation,
      o.inventoryPackageName,
      o.wellSpacingFt,
    ])
    .sort((a, b) => {
      const byArea = Number(a[0]) - Number(b[0]);
      return byArea !== 0 ? byArea : String(a[1]).localeCompare(String(b[1]));
    });
  return JSON.stringify(rows);
}

export function scenarioField(
  typeCurveScenarioId: number | null | undefined
): { typeCurveScenarioId: number } | Record<string, never> {
  return typeof typeCurveScenarioId === 'number' ? { typeCurveScenarioId } : {};
}

/**
 * Text for the Slot ID column: the well's UWI (`slotIdDisplay`, set by the BE
 * only for wellbore assets) when present, else the row identity `slotId`. `||`
 * (not `??`) so a blank display value also falls back. Kept as a named helper so
 * the fallback is unit-tested rather than buried in an inline cell renderer.
 *
 * @param {TractNRI} row - The slot / allocation row.
 * @return {string | undefined} The text to render in the Slot ID cell.
 */
export function slotIdDisplayText(row: TractNRI): string | undefined {
  return row.slotIdDisplay || row.slotId;
}

/**
 * Link target for the Type Curve Area cell: the type curve that priced this slot.
 *
 * The area itself has no page — /type_curve_areas/:id is a JSON-only endpoint and
 * 406s in a browser — so the cell points at the type curve instead, which is the
 * thing a reader actually wants from a slot row. `volumeSourceId` IS the curve's
 * tc_public_id for a type-curve slot, so no extra field is needed.
 *
 * Gated on typeCurveAreaName rather than volumeSourceId alone: a PDP slot also
 * carries a volumeSourceId, and that id is not a type curve (verified — a PDP
 * volume source resolves to no HeaderDetail). LinkCell then renders plain text
 * for any row this returns undefined for.
 *
 * /type_curve_headers/:public_id 301s to the canonical /type_curves/:public_id;
 * it is the version-stable form the app mints elsewhere.
 *
 * @param {TractNRI} row - The table row.
 * @return {string | undefined} The type-curve URL, or undefined when the row has none.
 */
export function typeCurveAreaLink(row: TractNRI): string | undefined {
  if (!row.typeCurveAreaName || !row.volumeSourceId) {
    return undefined;
  }
  return routes.typeCurveHeaderDetailsPath.replace(':id', row.volumeSourceId);
}

/**
 * Link target for the Slot ID / Well Name cells. Prefers a BE-provided
 * `slotIdHref` (the legacy pattern — the BE bakes the destination in); otherwise
 * builds it from the slot kind:
 *   - well slot → `/wells/:uwi` — the slot id *is* the UWI (`Well#to_param`), so
 *     it links to the well page.
 *   - LTD (well-less) slot → `/development_areas/:devAreaId` — its id is the
 *     composite `devArea_formation_slot`, so it links to the unit's
 *     development-area page, but only when the row carries `devAreaId` (the BE
 *     does not project it yet → no link until it does).
 *
 * @param {TractNRI} row - The slot / allocation row.
 * @return {string | undefined} The href, or `undefined` when no link can be built.
 */
export function slotIdLink(row: TractNRI): string | undefined {
  if (row.slotIdHref) {
    return row.slotIdHref;
  }
  if (row.wellStatus === 'LTD') {
    return row.devAreaId
      ? `/development_areas/${encodeURIComponent(row.devAreaId)}`
      : undefined;
  }
  return row.slotId ? `/wells/${encodeURIComponent(row.slotId)}` : undefined;
}

/**
 * Renders a table cell as a link (opening in a new tab) when both the value and
 * href are present, otherwise plain text (or the empty indicator).
 *
 * Thin positional adapter over the LinkCell component (components/link-cell),
 * kept because ~19 table  renderers call it positionally. NEW code should
 * use <LinkCell value href /> directly. Shared by the
 * Slot and Slot Allocation views' link columns (Slot ID / Well Name / CRM
 * Account / Unit) and the Uncovered drill-in tables, so the link treatment stays
 * in one place. Accepts `null` too (the uncovered rows' nullable fields) — the
 * body treats null and undefined identically.
 *
 * @param {string | number | null} [value] - The cell text.
 * @param {string | null} [href] - The link target.
 * @return {JSX.Element | string} The link or fallback text.
 */
export function linkCellRenderer(
  value: string | number | null | undefined,
  href: string | null | undefined
) {
  return <LinkCell value={value} href={href} empty={EMPTY} />;
}

/**
 * The Well Status column, shared by the Slot, Slot Allocation and Asset-expanded
 * slot grids.
 *
 * The row value stays the STORED status (`shut_in`, `expired_permit`, `LTD`) —
 * which is what sorting, the column filter and any server predicate see — and the
 * label is applied in the cell only. An unmapped status renders raw rather than
 * blank (see `wellStatusLabel`); a missing one renders the empty indicator.
 *
 * @return {object} The DataTable column definition.
 */
export function wellStatusColumn<
  T extends Record<string, unknown>,
>(): DataTableColumnDef<T> {
  return {
    accessorKey: 'wellStatus',
    header: 'Well Status',
    size: 130,
    meta: { type: 'text' },
    cell: (info) => wellStatusLabel(info.getValue<string | null>()) || EMPTY,
  };
}

interface LocateCellProps {
  /** The DataTable row id (matches what `locatedRowAtom` stores). */
  rowId: string;
  /** The row's related-geometry identifiers — what drives the map highlight. */
  mapElements: MapElements | undefined;
  /** Per-child geometry, carried so the subtable checkboxes can exclude children. */
  childHighlights: ChildHighlight[] | undefined;
  /** The row's bounding box for camera framing, or `undefined` when geometry is missing. */
  bbox: BBox | undefined;
}

/**
 * Pin cell for the locate column. Reads its own pinned-boolean off
 * `locatedRowAtom` to render filled vs outline, and on click dispatches `clear`
 * (when already pinned — unpin) or `pin` (otherwise — bulk, all children
 * included). The pin
 * is enabled whenever the row carries `mapElements` (its related slots/tracts/
 * units), which is what the map highlight filters on; `bbox` is only for framing
 * the camera and may be absent (a geometry-less row still highlights). A component
 * (not an inline cell) so it can use the atom hooks per row.
 *
 * @param {LocateCellProps} props - The row id, its mapElements, and its bbox.
 * @return {JSX.Element} The pin button (disabled when the row has no mapElements).
 */
function LocateCell({
  rowId,
  mapElements,
  childHighlights,
  bbox,
}: LocateCellProps) {
  // Subscribe each cell to ONLY its own pinned-boolean (derived per row), not the
  // whole collection — so a pin click re-renders just the previously- and
  // newly-pinned cells, not every pin cell on the page.
  const isLocatedAtom = useMemo(
    () =>
      selectAtom(locatedRowAtom, (rows) =>
        rows.some((row) => row.id === rowId)
      ),
    [rowId]
  );
  const isLocated = useAtomValue(isLocatedAtom);
  const dispatch = useSetAtom(locatedRowAtom);
  // Always render the icon so the column stays present; enabled when the row
  // carries `mapElements` (the highlight payload). Every parent row does, so the
  // pin is effectively always actionable.
  const canLocate = Boolean(mapElements);
  return (
    <Button
      kind="ghost"
      hasIconOnly
      size="sm"
      renderIcon={isLocated ? LocationFilled : Location}
      iconDescription={
        canLocate
          ? isLocated
            ? 'Hide on map'
            : 'Show on map'
          : 'No location available'
      }
      tooltipPosition="right"
      disabled={!canLocate}
      // The pin icon is the BULK control: clicking an unpinned row pins it with
      // every child included (`pin` → all checked); clicking the pinned row again
      // unpins it (`clear`). The `mapElements` guard narrows the type and is moot
      // when disabled. `childHighlights`/`bbox` ride along for the subtable
      // exclusions + camera framing.
      onClick={() => {
        if (!mapElements) {
          return;
        }
        if (isLocated) {
          dispatch({ type: 'clear' });
        } else {
          dispatch({
            type: 'pin',
            row: {
              id: rowId,
              mapElements,
              childHighlights: childHighlights ?? [],
              excludedChildIds: [],
              bbox,
            },
          });
        }
      }}
    />
  );
}

/**
 * Leading "show on map" action column shared by all three Master Data tables —
 * the frozen pin column from the mock UI. The cell is self-contained: it reads
 * and writes `locatedRowAtom` directly (no props threaded from the page), so a
 * view just prepends `locateColumn<Row>()` to its columns and the page reads the
 * same atom to frame the map.
 *
 * `mapElements` (the highlight payload) and `bbox` (`[minLng, minLat, maxLng,
 * maxLat]`, for framing) are BE row decorations not on the domain row type, so
 * they're read off the row's index signature.
 *
 * @return {object} The DataTable column definition for the pin column.
 */
export function locateColumn<
  TData extends Record<string, unknown>,
>(): DataTableColumnDef<TData> {
  return {
    id: '_locate',
    header: '',
    size: 64,
    enableSorting: false,
    cell: (info) => (
      <LocateCell
        rowId={info.row.id}
        mapElements={info.row.original.mapElements as MapElements | undefined}
        childHighlights={
          info.row.original.childHighlights as ChildHighlight[] | undefined
        }
        bbox={info.row.original.bbox as BBox | undefined}
      />
    ),
  };
}

/**
 * DataTable config for an expanded-row nested table: no toolbar / pagination /
 * sort / filter / visibility chrome, exact column widths (`fitContent`), and no
 * footer — just header + rows. Shared by every nested table in the feature.
 */
export const NESTED_TABLE_CONFIG: DataTableConfig = {
  globalSearch: false,
  columnVisibility: false,
  columnFilters: false,
  sorting: false,
  columnReordering: false,
  columnResizing: false,
  columnPinning: false,
  hideFooter: true,
  fitContent: true,
};

/**
 * `NESTED_TABLE_CONFIG` plus per-row multi-select with **no** select-all header —
 * the shared config for the expanded-row subtables whose checkboxes drive (the
 * geometry subtables) or mirror (the inert subtract subtables) the pinned row's map
 * highlight.
 */
export const NESTED_TABLE_MULTISELECT_CONFIG: DataTableConfig = {
  ...NESTED_TABLE_CONFIG,
  selectionType: 'multi',
  isBulkSelectEnabled: false,
};

/** `meta` for a variable-precision (0–8 dp) numeric column — matches `formatVariable`. */
export const DECIMAL_VARIABLE: DataTableColumnMeta = {
  type: 'decimal',
  format: { minDecimalPlaces: 0, maxDecimalPlaces: 8 },
};

/**
 * `meta` for a 2-dp percentage column (value ×100) — matches the source
 * `formatPercentage`, except DataTable's percent formatter always groups with
 * commas (the source does not). Only diverges above 1000%, which these coverage
 * / factor columns never reach, so the difference is immaterial in practice.
 */
export const PERCENT_2: DataTableColumnMeta = {
  type: 'percent',
  format: { multiplier: 100, minDecimalPlaces: 2, maxDecimalPlaces: 2 },
};

/**
 * `meta` for an 8-decimal numeric column — DataTable's `meta.type` decimal
 * formatter with fixed 8 places, identical output to `format8` from
 * `utils/number`. Shared so the acreage / NRI / WI columns stay in lockstep.
 */
export const DECIMAL_8: DataTableColumnMeta = {
  type: 'decimal',
  format: { minDecimalPlaces: 8, maxDecimalPlaces: 8 },
};

/** Display label per `TractNRI.assetType`; anything else falls back to "Tract". */
const ASSET_TYPE_LABELS: Record<string, string> = {
  wellbore: 'Wellbore',
  ltd_slot: 'LTD Slot',
};

/**
 * Humanizes the `assetType` enum, matching the source NRI table (`wellbore` →
 * "Wellbore", `ltd_slot` → "LTD Slot", anything else → "Tract"). Shared so the
 * Slot view and the nested slot-allocation tables render it identically.
 *
 * @param {string | null | undefined} value - The raw `assetType` enum value.
 * @return {string} The display label.
 */
export function formatAssetType(value: string | null | undefined): string {
  return (value && ASSET_TYPE_LABELS[value]) || 'Tract';
}

/**
 * Page-size options for the Master Data tables — must match the BE's allowed set
 * (`Queries::Packages::MasterDataController::ALLOWED_PAGE_SIZES`); any other size
 * is rejected with a 422. Passed to `<DataTable pageSizes>` (the app-wide default
 * `[10, 25, 50, 100]` would 422 on 10/25).
 */
export const MASTER_DATA_PAGE_SIZES = [50, 100, 250, 500];

/** Master-data views in toolbar-dropdown order. */
export const MASTER_DATA_VIEWS: MasterDataView[] = [
  'assets',
  'slots',
  'allocations',
];

/** Human-readable label per master-data view. */
export const MASTER_DATA_VIEW_LABELS: Record<MasterDataView, string> = {
  assets: 'Assets',
  slots: 'Properties',
  allocations: 'Property Allocations',
};

/**
 * Resolves the display label for a master-data view, tolerating the `null` the
 * Carbon Dropdown `itemToString` contract can pass. Module-scoped so the panel
 * does not allocate a fresh closure per render.
 *
 * @param {MasterDataView | null} view - The view to label, or `null`.
 * @return {string} The view's label, or an empty string for `null`.
 */
export function masterDataViewLabel(view: MasterDataView | null): string {
  return view ? MASTER_DATA_VIEW_LABELS[view] : '';
}

/**
 * Translates TanStack Table's sorting state into the BE `sort` wire shape
 * (`{ field, dir }`). Only the primary (first) sort entry is used (single-column
 * sort in P1). The column id is the FE accessor, so it's translated to the BE row
 * key via the per-view row-field renames (`toBeSortField`) — the one place the
 * row-shape mapping applies on the request side.
 *
 * @param {SortingState} sorting - TanStack sorting state (array of `{ id, desc }`).
 * @param {MasterDataView} view - The active view (selects the rename set).
 * @return {MasterDataSort | null} The primary sort, or `null` when unsorted.
 */
export function deriveSort(
  sorting: SortingState,
  view: MasterDataView
): MasterDataSort | null {
  const primary = sorting[0];
  if (!primary) {
    return null;
  }
  return {
    field: toBeSortField(view, primary.id),
    dir: primary.desc ? 'desc' : 'asc',
  };
}

// ─────────────────────────────────────
// Inline column-filter translation (INS-11272)
// ─────────────────────────────────────

/**
 * A column's stable id: TanStack's own resolution (`id ?? accessorKey`). This is
 * the id carried by every `ColumnFiltersState` entry, so it's the key both filter
 * translators map back to the column's `meta`.
 *
 * @param {DataTableColumnDef} col - The column definition.
 * @return {string | undefined} The column id, or `undefined` for a display column with neither.
 */
function columnDefId<TData>(
  col: DataTableColumnDef<TData>
): string | undefined {
  const explicit = (col as { id?: string }).id;
  if (explicit) return explicit;
  const accessorKey = (col as { accessorKey?: unknown }).accessorKey;
  return typeof accessorKey === 'string' ? accessorKey : undefined;
}

/**
 * Indexes a view's columns by id → `meta`, for the two filter translators.
 *
 * @param {DataTableColumnDef[]} columns - The columns to index.
 * @return {Map<string, DataTableColumnMeta | undefined>} id → meta lookup.
 */
function columnMetaById<TData>(
  columns: DataTableColumnDef<TData>[]
): Map<string, DataTableColumnMeta | undefined> {
  const byId = new Map<string, DataTableColumnMeta | undefined>();
  for (const col of columns) {
    const id = columnDefId(col);
    if (id) byId.set(id, col.meta as DataTableColumnMeta | undefined);
  }
  return byId;
}

/**
 * Divides a displayed range bound back to the RAW stored value the BE compares
 * against — the inverse of the column's display `multiplier` (e.g. a percent
 * column shows `15` for a raw `0.15`). `null`/`undefined` bounds stay open-ended.
 *
 * @param {number | null} value - The displayed bound (or `null` for open-ended).
 * @param {number} multiplier - The column's display multiplier (1 when none).
 * @return {number | undefined} The raw bound, or `undefined` when open-ended.
 */
function toRawBound(
  value: number | null,
  multiplier: number
): number | undefined {
  if (value === null) return undefined;
  return multiplier === 1 ? value : value / multiplier;
}

/**
 * Translates the DataTable's inline `ColumnFiltersState` into `master_data`
 * `tableFilters` predicates (INS-11272). Each entry maps by its column `meta.type`
 * (via `getFilterVariant`): enum/boolean → `selectedValues`; numeric → a RAW
 * `range` (display multiplier undone); date → an inclusive `range` (a `datetime`
 * upper bound is pushed to end-of-day so same-day rows survive the BE's lexical
 * compare); text → a case-insensitive substring `contains` (INS-11272 — so typing
 * "rush" matches "Rush Creek Minerals", not only an exact "rush"). The `attribute`
 * is the BE projected-row
 * key, produced by the SAME FE→BE rename that `deriveSort` uses (`toBeSortField`),
 * so a column filters on the same key it sorts on. Empty predicates are dropped.
 *
 * @param {ColumnFiltersState} columnFilters - The table's active inline filters.
 * @param {DataTableColumnDef[]} columns - The view's columns (for `meta` + id).
 * @param {MasterDataView} view - The active view (selects the rename set).
 * @return {TableFilter[]} The wire predicates (empty when nothing is filtered).
 */
export function toTableFilters<TData>(
  columnFilters: ColumnFiltersState,
  columns: DataTableColumnDef<TData>[],
  view: MasterDataView
): TableFilter[] {
  const metaById = columnMetaById(columns);
  return columnFilters.flatMap(({ id, value }) => {
    const meta = metaById.get(id);
    const attribute = toBeSortField(view, id);
    const predicate = tableFilterPredicate(attribute, meta, value);
    return predicate ? [predicate] : [];
  });
}

/**
 * One `tableFilters` predicate from a single column filter value, or `null` when
 * the value is empty (an all-clear the BE would treat as a no-op anyway).
 *
 * @param {string} attribute - The BE projected-row key.
 * @param {DataTableColumnMeta} meta - The column's meta (drives the variant).
 * @param {unknown} value - The TanStack column-filter value.
 * @return {TableFilter | null} The predicate, or `null` when empty.
 */
function tableFilterPredicate(
  attribute: string,
  meta: DataTableColumnMeta | undefined,
  value: unknown
): TableFilter | null {
  switch (getFilterVariant(meta)) {
    case 'multiselect': {
      const values = asStringList(value);
      return values.length ? { attribute, selectedValues: values } : null;
    }
    case 'range': {
      const [min, max] = value as [number | null, number | null];
      const multiplier = meta?.format?.multiplier ?? 1;
      const range = numericRange(min, max, multiplier);
      return range ? { attribute, range } : null;
    }
    case 'date': {
      const [start, end] = value as [string | null, string | null];
      const range = dateRange(start, end, meta?.type);
      return range ? { attribute, range } : null;
    }
    case 'text':
    default: {
      const text = typeof value === 'string' ? value.trim() : '';
      // A case-insensitive substring match (INS-11272) — the BE applies `contains`
      // over the projected rows, so "rush" matches "Rush Creek Minerals".
      return text ? { attribute, contains: text } : null;
    }
  }
}

/**
 * Translates the DataTable's inline `ColumnFiltersState` into `cashflows_by_month`
 * warehouse column filters (INS-11272). The cashflows warehouse DOES support
 * substring, so text → `contains`; enum/boolean → `in`; numeric/date ranges expand
 * to `gte`/`lte` bound predicates (numeric undoes the display multiplier). The
 * `column` is the camelCase warehouse key (no rename — the BE underscores it, as
 * for sort). Each column can emit 0–2 predicates; empty bounds are dropped.
 *
 * @param {ColumnFiltersState} columnFilters - The table's active inline filters.
 * @param {DataTableColumnDef[]} columns - The table's columns (for `meta` + id).
 * @return {WarehouseColumnFilter[]} The `{column, op, value}` predicates.
 */
export function toWarehouseColumnFilters<TData>(
  columnFilters: ColumnFiltersState,
  columns: DataTableColumnDef<TData>[]
): WarehouseColumnFilter[] {
  const metaById = columnMetaById(columns);
  return columnFilters.flatMap(({ id, value }) =>
    warehouseColumnPredicates(id, metaById.get(id), value)
  );
}

/**
 * The 0–2 warehouse predicates for a single column filter value.
 *
 * @param {string} column - The warehouse column (camelCase; BE underscores it).
 * @param {DataTableColumnMeta} meta - The column's meta (drives the variant).
 * @param {unknown} value - The TanStack column-filter value.
 * @return {WarehouseColumnFilter[]} 0–2 predicates.
 */
function warehouseColumnPredicates(
  column: string,
  meta: DataTableColumnMeta | undefined,
  value: unknown
): WarehouseColumnFilter[] {
  switch (getFilterVariant(meta)) {
    case 'multiselect': {
      const values = asStringList(value);
      return values.length ? [{ column, op: 'in', value: values }] : [];
    }
    case 'range': {
      const [min, max] = value as [number | null, number | null];
      const multiplier = meta?.format?.multiplier ?? 1;
      const lo = toRawBound(min, multiplier);
      const hi = toRawBound(max, multiplier);
      const out: WarehouseColumnFilter[] = [];
      if (lo !== undefined) out.push({ column, op: 'gte', value: lo });
      if (hi !== undefined) out.push({ column, op: 'lte', value: hi });
      return out;
    }
    case 'date': {
      const [start, end] = value as [string | null, string | null];
      const out: WarehouseColumnFilter[] = [];
      if (start) out.push({ column, op: 'gte', value: start });
      if (end) out.push({ column, op: 'lte', value: end });
      return out;
    }
    case 'text':
    default: {
      const text = typeof value === 'string' ? value.trim() : '';
      return text ? [{ column, op: 'contains', value: text }] : [];
    }
  }
}

/**
 * The non-empty string members of a multi-select filter value.
 *
 * @param {unknown} value - The TanStack multi-select filter value.
 * @return {string[]} The selected string values (empty when none).
 */
function asStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && v !== '')
    : [];
}

/**
 * An inclusive numeric `FilterRange` in RAW units (multiplier undone), or `null`
 * when both bounds are open.
 *
 * @param {number | null} min - The displayed lower bound (or `null`).
 * @param {number | null} max - The displayed upper bound (or `null`).
 * @param {number} multiplier - The column's display multiplier (1 when none).
 * @return {FilterRange | null} The raw range, or `null` when fully open.
 */
function numericRange(
  min: number | null,
  max: number | null,
  multiplier: number
): FilterRange | null {
  const lo = toRawBound(min, multiplier);
  const hi = toRawBound(max, multiplier);
  if (lo === undefined && hi === undefined) return null;
  return { min: lo, max: hi };
}

/**
 * An inclusive date `FilterRange` (ISO strings), or `null` when both bounds are
 * open. A `datetime` column's upper bound is pushed to end-of-day (UTC) so a
 * same-day timestamp isn't lexically dropped by the BE's string compare.
 *
 * @param {string | null} start - The lower bound (ISO date, or `null`).
 * @param {string | null} end - The upper bound (ISO date, or `null`).
 * @param {ColumnType | undefined} type - The column type (`datetime` expands the upper bound).
 * @return {FilterRange | null} The date range, or `null` when fully open.
 */
function dateRange(
  start: string | null,
  end: string | null,
  type: ColumnType | undefined
): FilterRange | null {
  if (!start && !end) return null;
  const max =
    end && type === 'datetime' ? `${end}T23:59:59.999Z` : end || undefined;
  return { min: start || undefined, max };
}

/**
 * Empty per-run case overrides — the case half of the working config's default,
 * the `setEvaluationCase` reset value (P4 FR#10), and the seed fallback for a run
 * whose configurations carry none. One constant so the shape is single-sourced;
 * consumers only ever spread it, never mutate.
 */
export const EMPTY_CASE_OVERRIDES: EvaluationCaseOverrides = {};

/**
 * The working-config form's `defaultValues`: an empty slot-override operations list, a
 * blank effective date, the "Legacy" DCA default (`null`), update-in-place
 * (`createNewEvalOnCalculate: false`), and the Case-management defaults — no case
 * (`evaluationCaseId: null`, which gates Calculate off, P4 FR#11), empty overrides,
 * and no explicit deck pick. `description` / `notes` are omitted (undefined) until
 * seeded. The seeding effect in `useWorkingConfigForm` overwrites the editable
 * fields — from the calc detail for an existing eval, or to today + create-new for
 * a from-scratch draft.
 */
export const defaultWorkingConfig: WorkingConfig = {
  effectiveDate: '',
  // Blank date + blank type = no discount-to override; the case's own anchor governs.
  discountToDate: '',
  discountToDateType: '',
  scenarioId: null,
  dcaScenarioId: null,
  typeCurveScenarioId: null,
  createNewEvalOnCalculate: false,
  evaluationCaseId: null,
  evaluationCaseOverrides: EMPTY_CASE_OVERRIDES,
  droppedAreaOverrides: false,
  priceDeckId: null,
  slotOverrides: [],
  scheduleSlotOverrides: [],
  committedSlotOverrides: [],
  interestItems: [],
};

/** The page/eval identifiers `buildCalculateRequest` needs beyond the working config. */
export interface CalculateRequestIds {
  /**
   * A case's run: what `run_scenarios` said it uses. Present, the run is
   * case-level: its overrides go out as `{ domain: id }` from these rows, with no area map, and it
   * sends no Type Curve or DCA (the BE takes both from the case). Absent, a run with a case
   * (`config.evaluationCaseId`) is still case-level, its `{ domain: id }` overrides read straight
   * off the form (a draft's previews, which never wait on `run_scenarios`); a run with no case is as
   * before.
   */
  caseLevelRows?: readonly RunScenarioRow[];
  /** The target workspace (the eval's owner). */
  workspaceId: number;
  /** The page's package. */
  packageId: number;
  /**
   * The eval being recalculated/forked — the route `:evalId`. OMITTED for a
   * from-scratch draft (package-mode Calculate creates a brand-new eval).
   */
  existingEvaluationId?: number;
  /**
   * Force a new-eval fork regardless of the form's `createNewEvalOnCalculate` value — set when the
   * viewed eval is SAVED, so recalculating it always creates a new eval and never mutates the saved
   * snapshot in place (P8 Save). No-op for a draft (already forced via the omitted id).
   */
  forceCreateNew?: boolean;
}

/**
 * Serializes the working-config form values into the `POST /evaluations/calculate`
 * request body (P5.md BE contract). A pure function over the form's VALUES (not the
 * TanStack form handle) so it's trivially testable and the form-library coupling stays
 * at the callsite — `useCalculate` passes `form.state.values`.
 *
 * Top-level fields are camelCase; `configurations` is the snake_case wire shape (the
 * case overrides are authored directly in snake_case, so they need no conversion). Slot
 * overrides serialize into `slot_overrides` as the eval's committed log followed by this
 * session's pending operations (`toSlotOverridesWire`), so a re-run resends the FULL
 * history — the BE merges `configurations`, so sending only pending ops would replace the
 * stored array and drop earlier overrides. Omitted when both are empty, so an unmodified
 * Calculate still sends a clean `configurations`. A fork carries the log forward too.
 * `existingEvaluationId` is included only when editing an existing
 * eval; `description` / `notes` ride top-level when non-empty; `dca_scenario_id` is
 * omitted for the "Legacy" (`null`) default.
 *
 * Case management (P4): `evaluation_case_id` + `evaluation_case_overrides` (the
 * `{ domain: id }` picks; never an earlier area-mode run's `areas` map) ride in
 * `configurations` whenever a case is selected — the Calculate gate requires one
 * (FR#11), so a real run always carries them; a null case omits both (mirrors the
 * top-level `scenarioId` posture).
 *
 * Price deck (P4 FR#9): the explicit per-run pick (`config.priceDeckId`) is sent in
 * TWO channels when made, and NOTHING is sent when it's null (the BE case-mode
 * runtime then uses the case's own deck natively — no FE fallback needed):
 *   1. inside `evaluation_case_overrides.price_deck_id` — the case-mode runtime
 *      resolves the deck as `overrides.price_deck_id → case.price_deck_id →
 *      configurations.price_deck_id`, so this is the ONLY channel that beats a case's
 *      own deck (a top-level pick alone loses to it — verified in
 *      `evaluation_calculation.rb`; see deferred-items.md);
 *   2. TOP-LEVEL `priceDeckId` — so the controller's `merge_price_deck_config`
 *      validates the deck is active (422 otherwise) and merges its id/name/methodType
 *      into the stored `configurations`. It merges only a deck the request names, so a
 *      run on the case's deck has none there. The FR#13 seed (seedPriceDeckId) reads a
 *      case run's pick back from channel 1, and a run with no case from here.
 * The wire's `evaluation_case_overrides.price_deck_id` is derived SOLELY from
 * `config.priceDeckId` here (the single source) — any stale value on the form's own
 * overrides (from a prior seed echo) is dropped by taking only the domain picks.
 *
 * @param {WorkingConfig} config - The working-config form's current values.
 * @param {CalculateRequestIds} ids - The workspace / package / optional existing-eval ids.
 * @return {CalculateRequest} The `POST /evaluations/calculate` request body.
 */
// Recursively camelCase object keys; arrays recurse, primitives (string values
// included) pass through untouched — only keys change.
function camelizeDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(camelizeDeep);
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[camelCase(key)] = camelizeDeep(val);
    }
    return out;
  }
  return value;
}

/**
 * Read-side boundary for the committed interest-modeling positions: the single place
 * that turns the snake_case `configurations.interest_items` blob into the camelCase
 * {@link InterestItem} shape the interest-modeling UI reads.
 *
 * Why this boundary exists. The write in `buildCalculateRequest` sends `interest_items`
 * with camelCase field keys, but two things make the READ snake_case: rails-boost
 * deep-underscores the whole request before it's stored, AND the `configurations` blob
 * is DELIBERATELY served snake_case (see the calc `show.json.jbuilder` comment — it is
 * intentionally NOT camelized the way `chart_config` is). So the committed blob echoes
 * back `tract_id`, `owner_wi`, `elected_election`, nested carry `groups`, …
 *
 * Why camelize at the boundary rather than read snake_case everywhere. Every other
 * field of `configurations` is consumed in its snake_case wire form — scalars are
 * read by their snake_case key, and the sibling `EvaluationCaseOverrides` blob is even
 * TYPED snake_case. Strict consistency would type `InterestItem` snake_case too, but it
 * is a broad camelCase surface already wired through every editor, the results, the
 * previews, and their tests; converting all of that is a far larger, uglier change than
 * one boundary camelize. So the wire contract stays snake_case (no server change) and
 * this is the ONE spot that bridges to the camelCase working shape. Seeding the raw blob
 * instead leaves every field `undefined` — blank tract labels, a stuck election editor,
 * no restored elected outcome. Only keys change; values (tract/well ids, trigger/variant
 * strings) pass through untouched.
 *
 * @param {unknown} raw - The committed `configurations.interest_items` (snake_case).
 * @return {InterestItem[]} The positions in the form's camelCase shape.
 */
export function deserializeInterestItems(raw: unknown): InterestItem[] {
  return (Array.isArray(raw) ? raw : []).map(
    (item) => camelizeDeep(item) as InterestItem
  );
}

/**
 * Whether the working interest-modeling positions have diverged from the eval's
 * committed `configurations.interest_items` blob. Derived from server truth rather
 * than form meta because the mid-run `form.reset` rebaseline (onRunCompleted) folds
 * concurrent edits into a pristine form — the values are kept but the dirty meta is
 * eaten, so the Calculate/Save gates go blind to interest-only edits. Comparing the
 * working array against the deserialized committed blob survives any meta loss, and
 * is stricter than meta-dirtiness: a perfectly reverted edit reads clean.
 *
 * @param {InterestItem[]} working - The form's current interestItems values.
 * @param {unknown} committedRaw - The committed `configurations.interest_items` (snake_case).
 * @return {boolean} True when the working items differ from the committed baseline.
 */
export function interestItemsDiverged(
  working: InterestItem[],
  committedRaw: unknown
): boolean {
  return !isEqual(working, deserializeInterestItems(committedRaw));
}

/**
 * The override log a Calculate sends as `slot_overrides`, and the coverage preview counts slots
 * under: INS-10777 — the FULL override history, the eval's already-committed log (hydrated in wire
 * shape, passed through untouched) followed by this session's pending operations. Both are required
 * because the BE MERGES configurations on a re-run — sending only the pending ops would replace the
 * stored array and drop every earlier override. Committed first preserves the timeline, so newer
 * edits still win field-by-field. INS-10671 — the schedule dialog's per-slot DATE ops (already
 * wire-shaped) ride the SAME `slot_overrides`, appended AFTER the slot-table ops so the individual
 * date edits pin last-wins.
 *
 * @param {object} config - The working config's three override arrays.
 * @return {Array} The log, in wire shape.
 */
export function runSlotOverridesWire(
  config: Pick<
    WorkingConfig,
    'committedSlotOverrides' | 'slotOverrides' | 'scheduleSlotOverrides'
  >
): Array<SlotOverrideWire | Record<string, unknown>> {
  return [
    ...config.committedSlotOverrides,
    ...toSlotOverridesWire(config.slotOverrides),
    ...config.scheduleSlotOverrides,
  ];
}

export function buildCalculateRequest(
  config: WorkingConfig,
  ids: CalculateRequestIds
): CalculateRequest {
  const {
    workspaceId,
    packageId,
    existingEvaluationId,
    forceCreateNew,
    caseLevelRows,
  } = ids;
  // A run with a case is always case-level: the BE takes its Type Curve and DCA from the case, so
  // no caller can send them by leaving a flag out.
  const caseLevel =
    caseLevelRows !== undefined || config.evaluationCaseId !== null;

  const slotOverridesWire = runSlotOverridesWire(config);

  // The wire's per-run overrides: the pruned area map (single-sourced from the
  // form) plus the explicit price-deck pick injected from `config.priceDeckId`
  // (the ONLY channel that beats the case's own deck at run time — see the JSDoc
  // + deferred-items.md). No pick → no `price_deck_id` (the runtime uses the
  // case's deck). Derived from `config.priceDeckId`, so a stale value on the
  // form's own overrides object (a prior seed echo) never leaks.
  // Discount-to-date rides in the case overrides — the channel the BE reads
  // (`evaluation_calculation.rb`). Sent snake_case (`discount_to_date`) like the
  // rest of this object: rails-boost underscores the inbound key anyway, so the
  // stored + served-back config uses snake — sending it snake keeps the 202
  // optimistic echo and the server refetch on the SAME key, so the seed reads it
  // back consistently (a camelCase key silently reset to the effective date once
  // the run finished and the snake-cased config came back).
  //
  // One wire shape per `discountToDateType` mode, mirroring the BE's
  // `override_type || case.discount_to_date_type` resolution:
  //
  //   ''         -> neither key. The CASE rung stays reachable: a 'fixed' case keeps
  //                 its own date and a 'floating' case stays floating. Materialising
  //                 the effective-date fallback here instead would make the override
  //                 unconditional and freeze a floating case to 'fixed' — the same
  //                 defect INS-11837 fixed on the read side.
  //   'fixed'    -> both keys. Guarded on a non-empty date: 'fixed' without one would
  //                 resolve `case_discount_to_date` against the CASE's date, so a
  //                 half-filled control would silently borrow an anchor it never showed.
  //   'floating' -> the type ALONE, deliberately with no date. This is the rung that
  //                 beats a 'fixed' case: the BE's `case_discount_to_date` returns nil
  //                 for a non-'fixed' type, and the calculator reads nil as "anchor on
  //                 the effective date".
  const { discountToDate, discountToDateType } = config;
  const discountToDateOverride =
    discountToDateType === 'fixed' && discountToDate
      ? {
          discount_to_date: discountToDate,
          discount_to_date_type: 'fixed' as const,
        }
      : discountToDateType === 'floating'
        ? { discount_to_date_type: 'floating' as const }
        : {};
  const evaluationCaseOverrides: EvaluationCaseOverrides = {
    ...(caseLevelRows !== undefined
      ? caseLevelOverrideWire(caseLevelRows)
      : caseLevel
        ? formDomainOverrides(config.evaluationCaseOverrides)
        : {}),
    ...(config.priceDeckId !== null
      ? { price_deck_id: config.priceDeckId }
      : {}),
    ...discountToDateOverride,
  };

  return {
    workspaceId,
    packageId,
    // With no existing eval to update in place (a from-scratch draft or a fresh
    // workspace with no eval yet), a run MUST create a new eval — the BE's reuse
    // branch does `EvaluationCalculation.find(existingEvaluationId)`, which errors
    // ("Couldn't find EvaluationCalculation without an ID") on a nil id. So force
    // the flag true here; the config value only chooses update-in-place vs. fork
    // when an existing eval IS present AND it isn't forced. `forceCreateNew` (a SAVED
    // eval) also forces a fork so its immutable snapshot is never recomputed in place.
    createNewEvalOnCalculate:
      existingEvaluationId === undefined || forceCreateNew
        ? true
        : config.createNewEvalOnCalculate,
    ...(existingEvaluationId !== undefined ? { existingEvaluationId } : {}),
    // The org-wide Scenario rides TOP-LEVEL (`scenarioId`), like legacy's `scenario_id`
    // — NOT in `configurations` (that's where the per-slot DCA scenario lives). Omitted
    // when unset (the Calculate gate requires one, so a run always carries it).
    ...(config.scenarioId !== null ? { scenarioId: config.scenarioId } : {}),
    // The explicit price-deck pick ALSO rides TOP-LEVEL, camelCase — the BE
    // `merge_price_deck_config` validates it's active (422 otherwise) and merges
    // id/name/methodType into the stored configurations (which the seed reads).
    // Omitted when there's no pick (the runtime falls back to the case's own deck).
    ...(config.priceDeckId !== null ? { priceDeckId: config.priceDeckId } : {}),
    ...(config.description ? { description: config.description } : {}),
    ...(config.notes ? { notes: config.notes } : {}),
    configurations: {
      effective_date: config.effectiveDate,
      // Omitted when no case is selected — the Calculate gate (FR#11) guarantees a
      // case on a real run, so this is the same gate-guaranteed posture as
      // `scenarioId` above. The overrides carry the pruned areas + the injected
      // price-deck pick (above).
      ...(config.evaluationCaseId !== null
        ? {
            evaluation_case_id: config.evaluationCaseId,
            evaluation_case_overrides: evaluationCaseOverrides,
          }
        : {}),
      ...(config.dcaScenarioId !== null && !caseLevel
        ? { dca_scenario_id: config.dcaScenarioId }
        : {}),
      // INS-10669 — the Type Curve Scenario the run's LTD places come from. Omitted when
      // unpicked so the BE applies its own Legacy default, the same one the pre-run tables
      // previewed; sending null here would assert a default the FE does not own.
      ...(config.typeCurveScenarioId !== null && !caseLevel
        ? { type_curve_scenario_id: config.typeCurveScenarioId }
        : {}),
      // INS-10777 — the unified override-operations array (the only override channel);
      // omitted when empty.
      ...(slotOverridesWire.length
        ? { slot_overrides: slotOverridesWire }
        : {}),
      // The curated interest-modeling positions ride the existing calc payload as an
      // opaque jsonb blob. Sent camelCase, but rails-boost deep-underscores the request,
      // so it's STORED and echoed back snake_case; `deserializeInterestItems` un-camelizes
      // it on reopen. Omitted when empty so an unmodeled run sends a clean config.
      ...(config.interestItems.length
        ? { interest_items: config.interestItems }
        : {}),
    },
  };
}

const AGGREGATION_UNIT: Record<AggregationKey, AggregationPeriod> = {
  monthly: 'month',
  quarterly: 'quarter',
  yearly: 'year',
};

/**
 * Maps the display `AggregationKey` (`monthly` / `quarterly` / `yearly`) to the moment
 * unit (`month` / `quarter` / `year`) used by the date-range math + period pickers.
 *
 * @param {AggregationKey} aggregation - The display aggregation.
 * @return {AggregationPeriod} The corresponding moment unit.
 */
export const aggregationUnit = (
  aggregation: AggregationKey
): AggregationPeriod => AGGREGATION_UNIT[aggregation];

/**
 * Resolves the visible date-range window from the display settings + the eval's
 * effective date — the relative offsets (N periods before/after) or the absolute
 * min/max, snapped to the aggregation. Pure (the legacy `useResolveDateRange` was a
 * hook only because it read per-package working atoms; here the caller passes the
 * slice it reads off `displaySettingsAtom`). `isSplit` (the effective date falling
 * mid-interval) is computed inline.
 *
 * NOTE (P5): no consumer yet — the date range is inert over Package Overview (its
 * `Pricing` rollup is aggregation-invariant). Shipped for the deferred Cash Flow /
 * Production Volumes time-series sections (P7).
 *
 * @param {string} effectiveDate - The eval's applied effective date (yyyy-MM-dd).
 * @param {string} aggregation - The active time aggregation (`AggregationKey`).
 * @param {object} dateRange - The relative/absolute range settings (`DisplaySettings['dateRange']`).
 * @return {object} The resolved `{ minDate, maxDate }` window (moment instances).
 */
export const resolveDateRange = (
  effectiveDate: string,
  aggregation: AggregationKey,
  dateRange: DisplaySettings['dateRange']
): { minDate: moment.Moment; maxDate: moment.Moment } => {
  const unit = aggregationUnit(aggregation);
  const effective = parseMomentUtc(effectiveDate).startOf('month');
  const isSplit = !effective.clone().startOf(unit).isSame(effective);

  const relativeMinDate = parseMomentUtc(effectiveDate)
    .startOf(unit)
    .subtract(dateRange.relative.pre, unit);
  const relativeMaxDate = parseMomentUtc(effectiveDate)
    .endOf(unit)
    .add(dateRange.relative.post - (isSplit ? 0 : 1), unit);

  if (
    dateRange.mode === 'absolute' &&
    dateRange.absolute.min &&
    dateRange.absolute.max
  ) {
    return {
      minDate: parseMomentUtc(dateRange.absolute.min).startOf(unit),
      maxDate: parseMomentUtc(dateRange.absolute.max).endOf(unit),
    };
  }
  return { minDate: relativeMinDate, maxDate: relativeMaxDate };
};

/**
 * The 16 raw volume arrays on the `eval_result` response mapped to the chart's
 * `ProductionVolumeEntry` shape: each gross/net stream to its base category, each
 * `totalCumulative*` pair to its cumulative category. The chart selects gross vs net at
 * render time by `measurementType`, so both are emitted here.
 */
const PRODUCTION_VOLUME_MAP: Array<{
  type: Exclude<ProductionVolumeEntry['type'], undefined>;
  category: ProductionType;
}> = [
  { type: 'grossOil', category: 'oil' },
  { type: 'netOil', category: 'oil' },
  { type: 'grossGasSold', category: 'gas' },
  { type: 'netGasSold', category: 'gas' },
  { type: 'grossNgl', category: 'ngl' },
  { type: 'netNgl', category: 'ngl' },
  { type: 'grossWater', category: 'water' },
  { type: 'netWater', category: 'water' },
  { type: 'totalCumulativeGrossOil', category: 'cumulativeOil' },
  { type: 'totalCumulativeNetOil', category: 'cumulativeOil' },
  { type: 'totalCumulativeGrossGasSold', category: 'cumulativeGas' },
  { type: 'totalCumulativeNetGasSold', category: 'cumulativeGas' },
  { type: 'totalCumulativeGrossNgl', category: 'cumulativeNgl' },
  { type: 'totalCumulativeNetNgl', category: 'cumulativeNgl' },
  { type: 'totalCumulativeGrossWater', category: 'cumulativeWater' },
  { type: 'totalCumulativeNetWater', category: 'cumulativeWater' },
];

/** The result-slice shape `toAggregatedProductionVolumes` reads (the 16 volume arrays live here). */
type EvalResultSlice = Exclude<
  AggregatedFreeCashFlowResultResponse['results'][number]['result'],
  null
>;

/**
 * Reshapes the raw `eval_result` volume arrays into the `AggregatedProductionVolumes` the
 * shared `AggregatedProductionVolumesChart` consumes — the eval3-new replica of the legacy
 * `convertToAggregatedFreeCashFlow` production-volume logic (NOT imported: legacy code stays
 * untouched). The response carries 16 per-period arrays (gross/net per stream + a
 * `totalCumulative*` pair each); this zips them against the period dates (selected by
 * `interval`) into a `ProductionVolumeEntry` per `(period, array)` cell, then — like the
 * legacy logic — GROUPS by `date+category+type` and collapses each group: SUM the
 * non-cumulative volumes, take the LAST row for the running `totalCumulative*` totals. A
 * period spans MULTIPLE rows (one per `slotCategory` — pdp/ltd/…), so the same
 * `date+category+type` recurs and those rows must be summed; Production Volumes has no
 * per-category legend, so the categories collapse into one stream.
 *
 * Guards: a missing `result` OR any of the 16 arrays absent → an empty
 * `{ productionVolumes: [], interval }` (the chart renders its no-data state).
 *
 * @param {EvalResultSlice | undefined} result - The non-null `results[0].result`, or undefined (no calc / missing).
 * @param {AggregationPeriod} interval - The time unit the data is aggregated at (from `aggregationUnit`).
 * @return {AggregatedProductionVolumes} The reshaped production volumes + the interval.
 */
export function toAggregatedProductionVolumes(
  result: EvalResultSlice | undefined,
  interval: AggregationPeriod
): AggregatedProductionVolumes {
  // The period buckets: whichever of year / quarter / month matches the interval.
  const dates =
    interval === 'year'
      ? result?.year
      : interval === 'quarter'
        ? result?.quarter
        : result?.month;

  if (
    !result ||
    !dates ||
    !PRODUCTION_VOLUME_MAP.every(({ type }) =>
      Array.isArray((result as Record<string, unknown>)[type])
    )
  ) {
    return { productionVolumes: [], interval };
  }

  // One raw entry per (period row × mapped type). A period spans MULTIPLE rows (one per
  // `slotCategory`), so several rows share the same `${date}-${category}-${type}` — they
  // MUST be collapsed, else the chart (which de-dupes points on that identity) silently
  // drops every row after the first and under-counts the period.
  const rawVolumes = dates.flatMap((date, index) =>
    PRODUCTION_VOLUME_MAP.map(({ type, category }) => ({
      date,
      category,
      type,
      volume:
        (result as unknown as Record<string, number[] | undefined>)[type]?.[
          index
        ] || 0,
    }))
  );

  // Collapse each `date+category+type` group (mirrors the legacy `convertToAggregatedFreeCashFlow`):
  // SUM the non-cumulative volumes across the slotCategory rows; for a running
  // `totalCumulative*` total take the LAST row (it's already period-cumulative, not additive).
  // `groupBy` preserves the response's row order within each group, so the last member is the
  // period's final `slotCategory` row (the grand-cumulative); a future re-sort would break this.
  const productionVolumes: ProductionVolumeEntry[] = Object.values(
    groupBy(rawVolumes, (v) => `${v.date}-${v.category}-${v.type}`)
  ).map((group) => {
    const isCumulative = group[0].type.startsWith('totalCumulative');
    const last = group[group.length - 1];
    return {
      date: isCumulative ? last.date : group[0].date,
      category: group[0].category,
      type: group[0].type,
      volume: isCumulative
        ? last.volume
        : group.reduce((sum, entry) => sum + entry.volume, 0),
    };
  });

  return { productionVolumes, interval };
}

/**
 * Reshapes the raw `eval_result` cashflow arrays into the `AggregatedFreeCashFlow` the shared
 * `AggregatedFreeCashFlowChart` + `FreeCashFlowCumulativeTable` consume — the eval3-new replica
 * of the legacy `convertToAggregatedFreeCashFlow` cashflow logic (NOT imported: legacy code
 * stays untouched). The response carries five per-(category × period) arrays
 * (`grossCashflow` / `netCashflow` / `slotCategory` / `cumulativeGrossCashflow` /
 * `cumulativeNetCashflow`); this zips them against the period dates (selected by `interval`)
 * into `FreeCashFlowEntry[]`, dropping any tuple with a missing date / category / value.
 * `packageOverview` (`{ maxPricing, targetPricing }`) and `rescatSlotCount` map straight from
 * the response, so the returned object is a faithful `AggregatedFreeCashFlow` (no fabricated
 * fields); `interval` + `effectiveDate` come from the arguments.
 *
 * Guards: a missing `result` OR any of the cashflow arrays absent → an empty
 * `{ freeCashflows: [], interval, effectiveDate, packageOverview: { maxPricing: [],
 * targetPricing: [] }, rescatSlotCount: [] }` (the chart renders its no-data state).
 *
 * @param {EvalResultSlice | undefined} result - The non-null `results[0].result`, or undefined (no calc / missing).
 * @param {AggregationPeriod} interval - The time unit the data is aggregated at (from `aggregationUnit`).
 * @param {string} effectiveDate - The eval's applied effective date (yyyy-MM-dd), or empty string.
 * @return {AggregatedFreeCashFlow} The reshaped cash flows + the interval / effective date / overview.
 */
export function toAggregatedFreeCashflows(
  result: EvalResultSlice | undefined,
  interval: AggregationPeriod,
  effectiveDate: string
): AggregatedFreeCashFlow {
  // The period buckets: whichever of year / quarter / month matches the interval.
  const dates =
    interval === 'year'
      ? result?.year
      : interval === 'quarter'
        ? result?.quarter
        : result?.month;

  if (
    !result ||
    !dates ||
    !Array.isArray(result.grossCashflow) ||
    !Array.isArray(result.netCashflow) ||
    !Array.isArray(result.slotCategory) ||
    !Array.isArray(result.cumulativeGrossCashflow) ||
    !Array.isArray(result.cumulativeNetCashflow)
  ) {
    return {
      freeCashflows: [],
      interval,
      effectiveDate,
      packageOverview: { maxPricing: [], targetPricing: [] },
      rescatSlotCount: [],
    };
  }

  // Zip the five per-(category × period) arrays against the period dates into one entry per
  // tuple, dropping any with a missing date / category / value (mirrors the legacy
  // `convertToAggregatedFreeCashFlow` cashflow build).
  const freeCashflows: FreeCashFlowEntry[] = dates
    .map((date, index) => {
      const grossCashflow = result.grossCashflow[index];
      const netCashflow = result.netCashflow[index];
      const category = result.slotCategory[index] as SlotCategory | undefined;
      const cumulativeGrossCashflow = result.cumulativeGrossCashflow[index];
      const cumulativeNetCashflow = result.cumulativeNetCashflow[index];
      if (
        !date ||
        grossCashflow === undefined ||
        netCashflow === undefined ||
        cumulativeGrossCashflow === undefined ||
        cumulativeNetCashflow === undefined ||
        !category
      ) {
        return null;
      }
      return {
        date,
        grossCashflow,
        netCashflow,
        category,
        cumulativeGrossCashflow,
        cumulativeNetCashflow,
      };
    })
    .filter((entry): entry is FreeCashFlowEntry => entry !== null);

  return {
    freeCashflows,
    interval,
    effectiveDate,
    packageOverview: {
      maxPricing: result.packageOverview?.maxPricing ?? [],
      targetPricing: result.packageOverview?.targetPricing ?? [],
    },
    rescatSlotCount: result.rescatSlotCount ?? [],
  };
}

/**
 * INS-10777 — serializes the ordered override operations to the BE wire shape
 * (`slot_overrides`), used on BOTH the master_data read body and the calculate
 * `configurations`. Override field keys pass through as-is (camelCase): the resolver
 * applies them onto the camelCase rows and `normalize_operation_overrides` accepts
 * camelCase. `filter` → `{ type:'bulk', scope, excluded_slot_ids }`; `ids` →
 * `{ type:'bulk', slot_ids }` (slot_ids short-circuits scope on the BE); `slot` →
 * `{ type:'individual', slot_id }`.
 *
 * @param {SlotOverrideOperation[]} operations - The ordered pending operations.
 * @return {SlotOverrideWire[]} The wire operations, in order.
 */
export function toSlotOverridesWire(
  operations: SlotOverrideLogEntry[]
): SlotOverrideWire[] {
  return operations.map((operation) => {
    // A `type_curve` entry is ALREADY in wire form — the TC Manager writes it through
    // `toTypeCurveOverrideWire` on Done, and it names an (area, formation) rather than a
    // slot, so it carries no `kind` to branch on. Pass it through untouched.
    //
    // Without this it falls past both `kind` checks to the scope-bulk branch below and
    // destructures an undefined `scope`, throwing on every request that carries one. That
    // stayed invisible for as long as nothing refetched after Done; it fires the moment
    // anything does.
    if (isTypeCurveOverrideEntry(operation)) {
      return operation;
    }
    // Same story for the spacing half: an `inventory_package` entry is written in wire form
    // by the TC Manager on Done and carries no `kind` to branch on (INS-10669).
    if (isInventoryPackageOverrideEntry(operation)) {
      return operation;
    }
    // REVERT. Handled before the setting shapes because it is the one
    // operation carrying `fields` instead of `overrides`; the branches below would
    // destructure an `overrides` it does not have.
    if (operation.kind === 'unset') {
      return {
        type: 'unset',
        slot_id: operation.slotId,
        fields: operation.fields,
      };
    }
    if (operation.kind === 'slot') {
      return {
        type: 'individual',
        slot_id: operation.slotId,
        overrides: operation.overrides,
      };
    }
    if (operation.kind === 'ids') {
      return {
        type: 'bulk',
        slot_ids: operation.slotIds,
        overrides: operation.overrides,
      };
    }
    const { search, filters, mapScope } = operation.scope;
    return {
      type: 'bulk',
      scope: {
        ...(filters?.length ? { filters } : {}),
        ...(search ? { search } : {}),
        // `mapscope` (lowercase) is the BE resolver's scope key — NOT a typo / not camelCase.
        ...(mapScope ? { mapscope: mapScope } : {}),
      },
      excluded_slot_ids: operation.excludedIds,
      overrides: operation.overrides,
    };
  });
}

/**
 * INS-10777 — optimistic paint of a SINGLE just-committed operation onto the rows currently
 * in the cache (the visible page), for zero-fetch instant feedback. Applies the operation's
 * `overrides` fields to each matching row and unions their keys into `overriddenFields`
 * (drives the ✓). A sparse `displayPatch` (e.g. the chosen AFE's well name + catalog href) is
 * painted too, but WITHOUT joining `overriddenFields` — it's display-only reconciliation, not a
 * user override. Remaining BE-derived cascades (AFE costs) still reconcile on the next
 * navigation read. Match: `slot` → that slot; `ids` → the id list; `filter` → every loaded row
 * not excluded (the visible rows already match the active filter, since a filter change is a
 * navigation read).
 *
 * @param {SlotRow[]} rows - The current page's (BE-resolved) rows.
 * @param {SlotOverrideOperation} operation - The just-committed operation.
 * @return {SlotRow[]} The rows with the operation painted on (unchanged rows kept by reference).
 */
/**
 * A display patch with its `null`s turned into `undefined`.
 *
 * `null` in a patch means CLEAR — the row must LOSE the value, not keep it. The
 * distinction matters on an AFE change: `afeSourceId` moves in the same operation, so an
 * AFE carrying no well name of its own has to wipe the displaced AFE's name rather than
 * leave it labelling the new source, and the same for the link behind it. Spreading the
 * `null` straight onto the row would clear it but leave the row disagreeing with its own
 * type, so it is normalised to `undefined` here: absent keys are still absent, and a
 * cleared column renders empty.
 *
 * @param {SlotDisplayPatch|undefined} patch - The operation's display patch, if it has one.
 * @return {object} The patch with `null`s normalised to `undefined`.
 */
function clearedDisplayPatch(patch: SlotDisplayPatch | undefined) {
  if (!patch) return {};

  return Object.fromEntries(
    Object.entries(patch).map(([key, value]) => [key, value ?? undefined])
  );
}

export function paintNewestOperation(
  rows: SlotRow[],
  operation: SlotOverrideOperation
): SlotRow[] {
  // ── A REVERT IS NOT PAINTED ────────────────────────────────────────────────────────────
  //
  // This function paints an operation's VALUES onto the visible rows for instant feedback. An
  // unset has no values — it removes them — and what it reveals underneath is whatever the
  // remaining log resolves to, which only the server can fold. Painting a guess here would
  // put a fabricated figure on the grid until the next read replaced it.
  //
  // So the revert's grid update is a REFETCH, not a paint: `commitSlotOverride` passes
  // `refetchType: 'active'` for the slots key on an unset alone (see the note there for what
  // that costs). Returning `rows` unchanged is "no optimistic paint", and it relies on that
  // refetch — marking the query stale is NOT enough on its own, because the grid the user is
  // looking at is the active read, and "refetch on next use" never comes while they watch.
  if (operation.kind === 'unset') {
    return rows;
  }
  const fields = Object.keys(operation.overrides) as (keyof OverrideFields)[];
  const displayKeys = Object.keys(operation.displayPatch ?? {});
  if (fields.length === 0 && displayKeys.length === 0) {
    return rows;
  }
  const idSet = operation.kind === 'ids' ? new Set(operation.slotIds) : null;
  const excludedSet =
    operation.kind === 'filter' ? new Set(operation.excludedIds) : null;
  const matches = (row: SlotRow): boolean => {
    const id = row.slotId ? String(row.slotId) : undefined;
    if (!id) {
      return false;
    }
    if (operation.kind === 'slot') {
      return id === operation.slotId;
    }
    if (operation.kind === 'ids') {
      return idSet!.has(id);
    }
    return !excludedSet!.has(id);
  };
  return rows.map((row) => {
    if (!matches(row)) {
      return paintLeg(row, operation);
    }
    const overriddenFields = Array.from(
      new Set([...(row.overriddenFields ?? []), ...fields])
    );
    // `displayPatch` paints AFTER `overrides` but never joins `overriddenFields`
    // (display-only reconciliation, no ✓); `overrides` still wins on any shared key.
    return {
      ...row,
      ...clearedDisplayPatch(operation.displayPatch),
      ...operation.overrides,
      overriddenFields,
    };
  });
}

/**
 * Paints a Volume Source override that names a composite wellbore's LEG (its own slot id, which
 * is no row) onto that leg: the new curve, its link and the ✓, for instant feedback. The row's
 * own source — the recipe rebuilt over the legs, with the new curve's lateral — only the server
 * can fold, so `commitSlotOverride` refetches the grid for a leg operation as it does for a revert.
 *
 * @param {SlotRow} row - A row the operation did not name.
 * @param {SlotOverrideOperation} operation - The operation just committed.
 * @return {SlotRow} The row, with the named leg painted, or unchanged.
 */
function paintLeg(row: SlotRow, operation: SlotOverrideOperation): SlotRow {
  if (operation.kind !== 'slot' || !row.compositeWellboreLegs) {
    return row;
  }
  const { volumeSourceId, volumeSourceHref } = operation.overrides;
  if (!volumeSourceId) {
    return row;
  }
  let painted = false;
  const legs = row.compositeWellboreLegs.map((leg) => {
    if (leg.slotId !== operation.slotId) {
      return leg;
    }
    painted = true;
    return {
      ...leg,
      volumeSourceId,
      volumeSourceHref: volumeSourceHref ?? null,
      overridden: true,
    };
  });
  return painted ? { ...row, compositeWellboreLegs: legs } : row;
}

/**
 * Reconciles the accumulated selected-row snapshots against the table's current
 * `toggledNodes` (slot ids): keeps an entry for every still-selected slot —
 * preferring the live row when the slot is on the current page (`liveById`),
 * else the prior snapshot — and drops any slot that was deselected. This is what
 * lets bulk selection persist across server-paginated pages: a row is always in
 * memory the moment it's ticked, so we capture it then and retain it after paging
 * away. Pure so it's unit-testable.
 *
 * @param {Record<string, SlotRow>} prev - The previous selected-row snapshot map.
 * @param {string[]} toggledNodes - The currently selected DataTable row ids (the BE `id`, which equals `slotId` for slots).
 * @param {Map<string, SlotRow>} liveById - Current-page rows keyed by slotId.
 * @return {Record<string, SlotRow>} The next selected-row snapshot map.
 */
export function reconcileSelectedRows(
  prev: Record<string, SlotRow>,
  toggledNodes: string[],
  liveById: Map<string, SlotRow>
): Record<string, SlotRow> {
  const next: Record<string, SlotRow> = {};
  let changed = false;
  toggledNodes.forEach((slotId) => {
    const row = liveById.get(slotId) ?? prev[slotId];
    if (row) {
      next[slotId] = row;
      if (prev[slotId] !== row) {
        changed = true;
      }
    }
  });
  // A removal (a slot in `prev` that's no longer selected) also counts as a
  // change even when no surviving entry's reference moved.
  if (!changed && Object.keys(prev).length !== Object.keys(next).length) {
    changed = true;
  }
  // Preserve the previous object's identity when nothing changed, so a
  // selection-unchanged `onSelectionChange` (e.g. fired during pagination) does
  // not churn `selectedById` and trigger downstream re-renders.
  return changed ? next : prev;
}

// ─── Real master_data BE adapter (row shape) ───
// The FE now speaks the BE's request/envelope/view vocabulary verbatim, so the
// ONLY FE↔BE translation left is the per-row field shape: the views/columns read
// the shared FE shapes (`CRMTractsResponse` / `TractNRI`), so each BE row is
// remapped to those. The per-row `children` is consumed for the Slot + Slot
// Allocation expansions (mapped to `subtracts` / `tractAllocations`); only the
// assets view leaves `children` untouched (its two-level shape is a later slice).

/**
 * FE-accessor → BE-row-key renames for the Slot + Slot Allocation views, used on
 * the REQUEST side only: `toBeSortField` translates a sorted column id to the BE
 * sort key. The same renames are mirrored field-by-field in `mapTractNriScalars`
 * (the response side) — if you add or change one here, mirror it there too.
 */
const SLOT_FIELD_RENAMES: Record<string, string> = {
  totalSlotNri: 'netRevenueInterest',
  totalSlotWi: 'netWorkingInterest',
  formation: 'formationName',
  operator: 'operatorName',
  crmTractSalesforceId: 'tractId',
};

/**
 * FE-accessor → BE-row-key renames for the Asset view, used on the REQUEST side
 * only (`toBeSortField`). Mirrored field-by-field in `mapAssetRow` (the response
 * side) — keep both in sync.
 */
const ASSET_FIELD_RENAMES: Record<string, string> = {
  name: 'crmTract',
  salesforceId: 'tractId',
  entities: 'insightsEntity',
  createdAt: 'createdDate',
  updatedAt: 'updatedDate',
  netAcres: 'tractNetAcres',
  grossAcres: 'tractGrossAcres',
  nri: 'netRevenueInterest',
  workingInterest: 'netWorkingInterest',
  // The picker-only Type column is an FE-only column id over the row's
  // instrumentType, so sort + inline filter both translate to that BE key. No
  // response-side mirror needed — the column reads `instrumentType` directly.
  type: 'instrumentType',
  // The Vesting Document column RENDERS the indicator hash but SORTS on the scalar status beside
  // it: the backend's sort_key stringifies anything non-numeric, so ordering on the hash would
  // compare its inspect form. No response-side mirror — the column reads `vestingDocument`
  // directly, and `vestingStatus` exists only to be sorted on.
  vestingDocument: 'vestingStatus',
};

/**
 * Slots-view-only sort renames, layered on top of SLOT_FIELD_RENAMES. The Slots
 * view's Slot ID column DISPLAYS `slotIdDisplay` (the UWI for wellbore assets),
 * so it must SORT on that value too — otherwise wellbore rows order by the hidden
 * composite id and the column looks non-monotonic. Slots-only: the Slot
 * Allocation view shows the composite `slotId`, so it keeps sorting on `slotId`.
 */
const SLOTS_VIEW_FIELD_RENAMES: Record<string, string> = {
  slotId: 'slotIdDisplay',
};

/**
 * Sort-only renames for columns that display a different row key than their own,
 * in both the Slots and Slot Allocation views. The Volume Source column is keyed
 * on `volumeSourceId` but shows `volumeSourceName` (a DCA forecast's business id),
 * so it sorts on the name.
 */
const DISPLAYED_SORT_FIELDS: Record<string, string> = {
  volumeSourceId: 'volumeSourceName',
};

/**
 * Translates a sort field from its FE column accessor to the BE row key for the
 * view (Asset uses the asset rename set, Slots layers its own over the slot set,
 * Slot Allocation uses the slot set, and both slot views sort displayed columns by
 * what they display); unmapped fields pass through unchanged.
 *
 * @param {MasterDataView} view - The view being sorted.
 * @param {string} feField - The FE column accessor.
 * @return {string} The BE row key to sort on.
 */
function toBeSortField(view: MasterDataView, feField: string): string {
  if (view === 'assets') {
    return ASSET_FIELD_RENAMES[feField] ?? feField;
  }
  if (view === 'slots') {
    return (
      SLOTS_VIEW_FIELD_RENAMES[feField] ??
      DISPLAYED_SORT_FIELDS[feField] ??
      SLOT_FIELD_RENAMES[feField] ??
      feField
    );
  }
  return (
    DISPLAYED_SORT_FIELDS[feField] ?? SLOT_FIELD_RENAMES[feField] ?? feField
  );
}

/**
 * Attaches the cross-cutting row decorations that aren't fields on the FE row
 * types (`TractNRI` / `SubtractNRI` / `AssetRow`) but ride the DataTable row's
 * index signature: `id` (the BE row identity, used directly as the row key —
 * always set), `bbox` (the pin geometry, for camera framing — only when the BE
 * sends it), and `mapElements` (the pin-to-highlight identifiers — only when
 * present). Returns the same row, decorated in place. One place for the
 * index-signature cast so all mappers attach these the same way.
 *
 * @param {T} row - The mapped FE row to decorate.
 * @param {object} meta - The BE-provided decorations.
 * @param {string | null} [meta.id] - The BE row id.
 * @param {BBox | null} [meta.bbox] - The row bounding box, when present.
 * @param {MapElements | null} [meta.mapElements] - The related-geometry identifiers, when present.
 * @return {T} The same row, decorated.
 */
function attachRowMeta<T>(
  row: T,
  meta: {
    id?: string | null;
    bbox?: BBox | null;
    mapElements?: MapElements | null;
  }
): T {
  const decorated = row as {
    id?: string;
    bbox?: BBox;
    mapElements?: MapElements;
  };
  decorated.id = meta.id ?? undefined;
  if (meta.bbox) {
    decorated.bbox = meta.bbox;
  }
  if (meta.mapElements) {
    decorated.mapElements = meta.mapElements;
  }
  return row;
}

/**
 * Formats the BE `depthRange` object (`{ startDepth, endDepth }`) into the FE
 * row's display string (e.g. "2,500 - 11,949"), reusing `formatRange` from
 * utils/number — the same formatter the legacy crm-tracts Depth Range column
 * uses. Returns undefined when there's no range, so the column renders blank
 * rather than "[object Object]" (the bug from passing the object through raw).
 *
 * @param {DepthRangeResponse | null} [range] - The raw BE depth range object.
 * @return {string | undefined} The formatted "start - end" string, or undefined.
 */
function formatDepthRange(
  range?: DepthRangeResponse | null
): string | undefined {
  if (!range) {
    return undefined;
  }
  return (
    formatRange({ start: range.startDepth, end: range.endDepth }) || undefined
  );
}

/**
 * Maps the shared scalar fields of a `slots` / `allocations` response row to the
 * FE `TractNRI`. One explicit line per field — read top-to-bottom to see exactly
 * what each column receives. The five renames at the top mirror `SLOT_FIELD_RENAMES`
 * (used by `toBeSortField` on the request side); keep both in sync. Null is
 * coalesced to `undefined` (downstream formatters/links treat them the same).
 * Tract-level fields are absent on the slots row (→ `undefined`) and present on
 * allocations. Child arrays (`subtracts` / `tractAllocations`) are attached by the
 * caller, not here.
 *
 * @param {TractNriRowResponse} res - One raw slot / allocation response row.
 * @return {TractNRI} The FE row (scalars only).
 */
function mapTractNriScalars(res: TractNriRowResponse): TractNRI {
  const row: TractNRI = {
    // ── renames (mirror SLOT_FIELD_RENAMES) ──
    // Bind the ROW's own interest: on slots that's the slot total, on allocations
    // the per-allocation (slot x tract) value — which the BE now resolves through
    // the shared certified rule (InterestCertification::TractInterest), so a
    // certified slot reads the same here as in the Slot Allocations export and the
    // rows sum back to the slot total instead of repeating it N times (INS-11417).
    // This also matches SLOT_FIELD_RENAMES, which already sorts this column by
    // net*Interest — previously the column sorted by one field and displayed
    // another. totalSlotNet*Interest stays the explicit slot roll-up fallback.
    totalSlotNri:
      res.netRevenueInterest ?? res.totalSlotNetRevenueInterest ?? undefined,
    totalSlotWi:
      res.netWorkingInterest ?? res.totalSlotNetWorkingInterest ?? undefined,
    wiSource: res.wiSource ?? undefined,
    nriCertifiedSource: res.nriCertifiedSource ?? undefined,
    // INS-11396 — carry the pill's link ids onto the row so the Total Slot
    // interest cell can wire the Certified / System Promoted pill to its target.
    nriCertifiedWorkspaceId: res.nriCertifiedWorkspaceId ?? undefined,
    interestEntityId: res.interestEntityId ?? undefined,
    formation: res.formationName ?? undefined,
    operator: res.operatorName ?? undefined,
    crmTractSalesforceId: res.tractId ?? undefined,
    heldInPart: res.heldInPart ?? undefined,
    subtractsHeld: res.subtractsHeld ?? undefined,
    subtractsInTract: res.subtractsInTract ?? undefined,
    // ── identity / links ──
    slotId: res.slotId ?? undefined,
    slotIdHref: res.slotIdHref ?? undefined,
    slotIdDisplay: res.slotIdDisplay ?? undefined,
    unit: res.unit ?? undefined,
    unitHref: res.unitHref ?? undefined,
    unitTags: res.unitTags ?? undefined,
    unitSource: res.unitSource ?? undefined,
    unitDevelopmentType: res.unitDevelopmentType ?? undefined,
    devAreaId: res.devAreaId ?? undefined,
    assetType: res.assetType ?? undefined,
    crmTract: res.crmTract ?? undefined,
    crmTractTags: res.crmTractTags ?? undefined,
    crmAccount: res.crmAccount ?? undefined,
    crmAccountHref: res.crmAccountHref ?? undefined,
    // ── volume / AFE source ──
    volumeSourceId: res.volumeSourceId ?? undefined,
    volumeSourceName: res.volumeSourceName ?? undefined,
    volumeSourceHref: res.volumeSourceHref ?? undefined,
    volumeSourceType: res.volumeSourceType ?? undefined,
    typeCurveVerificationStatus: res.typeCurveVerificationStatus ?? undefined,
    typeCurveLastVerifiedBy: res.typeCurveLastVerifiedBy ?? undefined,
    compositeWellboreId: res.compositeWellboreId ?? undefined,
    compositeWellboreName: res.compositeWellboreName ?? undefined,
    compositeWellboreLegs: res.compositeWellboreLegs ?? undefined,
    afeSourceId: res.afeSourceId ?? undefined,
    afeSourceHref: res.afeSourceHref ?? undefined,
    afeWellName: res.afeWellName ?? undefined,
    // ── scalars ──
    interestType: res.interestType ?? undefined,
    wellName: res.wellName ?? undefined,
    wellStatus: res.wellStatus ?? undefined,
    slotNumber: res.slotNumber ?? undefined,
    holeDirection: res.holeDirection ?? undefined,
    typeCurveStatus: res.typeCurveStatus ?? undefined,
    forecastStatus: res.forecastStatus ?? undefined,
    forecastDate: res.forecastDate ?? undefined,
    dcaScenario: res.dcaScenario ?? undefined,
    // Scenario-area display names. All FIVE travel together — they are captured
    // by ScenarioAreaResolution and baked into the same blob — so a column left
    // out here renders an em-dash no matter what the BE resolved. expenseArea was
    // mapped alone, which is why Expense Area filled while its four siblings
    // stayed blank on an eval whose payload carried all five.
    expenseArea: res.expenseArea ?? undefined,
    dpsArea: res.dpsArea ?? undefined,
    dpsAreaScheduled: res.dpsAreaScheduled ?? undefined,
    taxArea: res.taxArea ?? undefined,
    differentialArea: res.differentialArea ?? undefined,
    discountRateArea: res.discountRateArea ?? undefined,
    dpsAreaHref: res.dpsAreaHref ?? undefined,
    taxAreaHref: res.taxAreaHref ?? undefined,
    differentialAreaHref: res.differentialAreaHref ?? undefined,
    discountRateAreaHref: res.discountRateAreaHref ?? undefined,
    expenseAreaHref: res.expenseAreaHref ?? undefined,
    stateAbbreviation: res.stateAbbreviation ?? undefined,
    basin: res.basin ?? undefined,
    tier: res.tier ?? undefined,
    trueVerticalDepthFeet: res.trueVerticalDepthFeet ?? undefined,
    slotLength: res.slotLength ?? undefined,
    typeCurveAreaName: res.typeCurveAreaName ?? undefined,
    typeCurveLateralLength: res.typeCurveLateralLength ?? undefined,
    // ── dates ──
    permitDate: res.permitDate ?? undefined,
    spudDate: res.spudDate ?? undefined,
    rigReleaseDate: res.rigReleaseDate ?? undefined,
    completionDate: res.completionDate ?? undefined,
    firstProductionDate: res.firstProductionDate ?? undefined,
    shutInDate: res.shutInDate ?? undefined,
    // ── costs ──
    slotDrillingCosts: res.slotDrillingCosts ?? undefined,
    slotCompletionCosts: res.slotCompletionCosts ?? undefined,
    slotFacilitiesCosts: res.slotFacilitiesCosts ?? undefined,
    slotContingenciesCosts: res.slotContingenciesCosts ?? undefined,
    slotOtherCosts: res.slotOtherCosts ?? undefined,
    drillingCostsDate: res.drillingCostsDate ?? undefined,
    completionCostsDate: res.completionCostsDate ?? undefined,
    facilitiesCostsDate: res.facilitiesCostsDate ?? undefined,
    contingenciesCostsDate: res.contingenciesCostsDate ?? undefined,
    otherCostsDate: res.otherCostsDate ?? undefined,
    // ── NPV ──
    targetNpvExcluded: res.targetNpvExcluded ?? undefined,
    maxNpvExcluded: res.maxNpvExcluded ?? undefined,
    targetNpvIncluded: res.targetNpvIncluded ?? undefined,
    maxNpvIncluded: res.maxNpvIncluded ?? undefined,
    // ── tract-level (allocations only; undefined on slots) ──
    allocationMethod: res.allocationMethod ?? undefined,
    tractNetAcres: res.tractNetAcres ?? undefined,
    tractGrossAcres: res.tractGrossAcres ?? undefined,
    netAcreageUnits: res.netAcreageUnits ?? undefined,
    surveyArea: res.surveyArea ?? undefined,
    quarterCall: res.quarterCall ?? undefined,
    instrumentType: res.instrumentType ?? undefined,
    depthRange: formatDepthRange(res.depthRange),
    titleQuality: res.titleQuality ?? undefined,
    titleRiskFactor: res.titleRiskFactor ?? undefined,
    // ── valuation ──
    valued: res.valued ?? undefined,
    unvaluedReason: res.unvaluedReason ?? undefined,
    unvaluedReasons: res.unvaluedReasons ?? undefined,
    landedWithoutFormation: res.landedWithoutFormation ?? undefined,
    landingInterval: res.landingInterval ?? undefined,
  };
  return attachRowMeta(row, {
    id: res.id,
    bbox: res.bbox,
    mapElements: res.mapElements,
  });
}

/**
 * Maps a `slots`-view response row to `SlotRow` (scalars only; the orchestrator
 * attaches `tractAllocations` + the flattened `subtracts`).
 *
 * @param {SlotRowResponse} res - One raw slots response row.
 * @return {SlotRow} The FE slot row.
 */
function mapSlotRow(res: SlotRowResponse): SlotRow {
  // `unitTractId` feeds the Volume Source picker's pair lookup (formation is already mapped);
  // `isWellSlot` decides which list it opens a row of unknown source type on.
  const row: SlotRow = {
    ...mapTractNriScalars(res),
    unitTractId: res.unitTractId,
    isWellSlot: res.isWellSlot,
  };
  // INS-10777 — pass through the BE per-row override flags (drives the ✓).
  return res.overriddenFields
    ? { ...row, overriddenFields: res.overriddenFields }
    : row;
}

/** What the Volume Source picker needs off a row to narrow its list. */
export interface RowVolumeSourcePair {
  unitId: number | null;
  formation: string | null;
  typeCurveAreaName: string | null;
  // The row's resolved volume source (override-or-base), which the picker tags as in use
  // and, when the row has no override, pre-selects (INS-13809).
  resolvedVolumeSourceId: string | null;
}

/**
 * Reads the picker's narrowing fields off an adapted slot row.
 *
 * Takes a plain `SlotRow` rather than `SlotTableRow`, whose index signature would let a
 * misspelled field (e.g. the BE's `formationName`) compile and silently read `undefined`.
 *
 * @param {SlotRow} row - An adapted slot row.
 * @return {RowVolumeSourcePair} The pair fields, each null when the row does not carry it.
 */
export function pairFromRow(row: SlotRow): RowVolumeSourcePair {
  return {
    unitId: row.unitTractId ?? null,
    formation: row.formation ?? null,
    typeCurveAreaName: row.typeCurveAreaName ?? null,
    resolvedVolumeSourceId: row.volumeSourceId ?? null,
  };
}

/**
 * Maps an `allocations`-view response row to `SlotAllocationRow` (scalars only;
 * the orchestrator attaches `subtracts`).
 *
 * @param {SlotAllocationRowResponse} res - One raw allocations response row.
 * @return {SlotAllocationRow} The FE slot-allocation row.
 */
function mapAllocationRow(res: SlotAllocationRowResponse): SlotAllocationRow {
  return mapTractNriScalars(res);
}

/**
 * Maps a tract-grain child (`children[]` on a slots row) to the `SlotAllocationRow`
 * shape the slot expansion's nested tract table reads. Standalone (a smaller,
 * tract-only field set) rather than routed through `mapTractNriScalars`.
 *
 * @param {TractChildResponse} res - One raw tract child.
 * @return {SlotAllocationRow} The FE tract-allocation row.
 */
function mapTractChild(res: TractChildResponse): SlotAllocationRow {
  const row: SlotAllocationRow = {
    crmTract: res.crmTract ?? undefined,
    crmTractTags: res.crmTractTags ?? undefined,
    crmTractSalesforceId: res.tractId ?? undefined,
    heldInPart: res.heldInPart ?? undefined,
    subtractsHeld: res.subtractsHeld ?? undefined,
    subtractsInTract: res.subtractsInTract ?? undefined,
    crmAccount: res.crmAccount ?? undefined,
    crmAccountHref: res.crmAccountHref ?? undefined,
    depthRange: formatDepthRange(res.depthRange),
    surveyArea: res.surveyArea ?? undefined,
    quarterCall: res.quarterCall ?? undefined,
    instrumentType: res.instrumentType ?? undefined,
    allocationMethod: res.allocationMethod ?? undefined,
    netAcreageUnits: res.netAcreageUnits ?? undefined,
    totalSlotNri: res.netRevenueInterest ?? undefined,
    totalSlotWi: res.netWorkingInterest ?? undefined,
    tractNetAcres: res.tractNetAcres ?? undefined,
    tractGrossAcres: res.tractGrossAcres ?? undefined,
    titleQuality: res.titleQuality ?? undefined,
    titleRiskFactor: res.titleRiskFactor ?? undefined,
  };
  return attachRowMeta(row, { id: res.id });
}

/**
 * Maps an `assets`-view response row to `AssetRow`. Explicit field-by-field (7
 * renames mirror `ASSET_FIELD_RENAMES`). The flat `crmAccount` string is reshaped
 * into the nested `account` object the CRM Account column reads. The `assets`
 * endpoint emits `depthRange` as the object `{ startDepth, endDepth }`, which
 * `formatDepthRange` turns into the FE `AssetRow`'s display string.
 *
 * @param {AssetRowResponse} res - One raw asset response row.
 * @return {AssetRow} The FE asset row.
 */
function mapAssetRow(res: AssetRowResponse): AssetRow {
  // Built as an untyped bag + a final `as unknown as AssetRow` (rather than a
  // typed `AssetRow` literal) for two reasons: (1) `AssetRow extends
  // CRMTractsResponse`, whose `salesforceHref` / `subtracts` are required on the
  // type but NOT emitted by the `assets` endpoint — they're intentionally left
  // unmapped (no eval3 Asset column reads them); (2) `account` is only partially
  // populated (name only). A typed literal would force faking the first set and
  // casting the second. `nri` / `workingInterest` are typed `number | null` there
  // but carry `undefined` for a tract with no subtract interest (blank cell).
  const row: Record<string, unknown> = {
    // ── renames (mirror ASSET_FIELD_RENAMES) ──
    name: res.crmTract ?? undefined,
    salesforceId: res.tractId ?? undefined,
    heldInPart: res.heldInPart ?? undefined,
    subtractsHeld: res.subtractsHeld ?? undefined,
    subtractsInTract: res.subtractsInTract ?? undefined,
    entities: res.insightsEntity ?? undefined,
    createdAt: res.createdDate ?? undefined,
    updatedAt: res.updatedDate ?? undefined,
    netAcres: res.tractNetAcres ?? undefined,
    grossAcres: res.tractGrossAcres ?? undefined,
    nri: res.netRevenueInterest ?? undefined,
    workingInterest: res.netWorkingInterest ?? undefined,
    // ── passthrough ──
    crmTractTags: res.crmTractTags ?? undefined,
    surveyArea: res.surveyArea ?? undefined,
    quarterCall: res.quarterCall ?? undefined,
    netAcreageUnits: res.netAcreageUnits ?? undefined,
    depthRange: formatDepthRange(res.depthRange),
    instrumentType: res.instrumentType ?? undefined,
    targetNpvExcluded: res.targetNpvExcluded ?? undefined,
    maxNpvExcluded: res.maxNpvExcluded ?? undefined,
    targetNpvIncluded: res.targetNpvIncluded ?? undefined,
    maxNpvIncluded: res.maxNpvIncluded ?? undefined,
    titleQuality: res.titleQuality ?? undefined,
    titleRiskFactor: res.titleRiskFactor ?? undefined,
    // Null is kept rather than coalesced to undefined like its neighbours: the column renders a
    // dash for "this tract has no deed", which is the ordinary answer for one that never came
    // through an acquisition, and both values reach that same branch. Explicit so a reader does
    // not take the deviation for an oversight.
    vestingDocument: res.vestingDocument ?? null,
    // The row's kind, why it adds nothing, and — for a wellbore or LTD row — where its name
    // links (the table lists every asset on the package, not only tracts on slots).
    assetKind: res.assetKind ?? undefined,
    noSlotsReason: res.noSlotsReason ?? undefined,
    assetHref: res.assetHref ?? undefined,
  };
  // The BE sends only the account name; the CRM Account column reads
  // `account?.name`. Guarded so a sparse asset doesn't get `{ name: undefined }`.
  // `AssetRow.account` is typed full `AccountResponse`, hence the `as unknown as`
  // below — don't read `account.salesforceHref`/`uuid`/`id` off an adapted row.
  if (res.crmAccount !== null && res.crmAccount !== undefined) {
    row.account = { name: res.crmAccount };
  }
  return attachRowMeta(row, {
    id: res.id,
    bbox: res.bbox,
    mapElements: res.mapElements,
  }) as unknown as AssetRow;
}

/**
 * Adapts one raw BE subtract-grain child (`children[]` on an allocation row) to
 * the shared `SubtractNRI` shape the expanded sub-table reads, flattening the
 * nested `bundleOfSticks` / `parameters` / `unitAllocationTract` objects.
 *
 * The BE emits `nriCalculationId`, so it's passed through (falling back to 0,
 * which is falsy and self-suppresses the popup) — this drives the sub-table's
 * fetch-by-id formula popups. UAT NRI / WI map to the subtract's allocated
 * `netRevenueInterest` / `netWorkingInterest` (the BE has no top-level
 * `nri` / `workingInterest`; `unitAllocationTract` is a descriptor, not an
 * interest carrier). `partTractAcres` / `ptfAdjustment` / `focusMdac` have no
 * BE source and stay undefined (render blank).
 *
 * @param {SubtractChildResponse} child - One raw subtract child.
 * @return {SubtractNRI} The flattened FE subtract row.
 */
function adaptSubtractChild(child: SubtractChildResponse): SubtractNRI {
  const bundle = child.bundleOfSticks ?? {};
  const params = child.parameters ?? {};
  const subtract: SubtractNRI = {
    nriCalculationId: child.nriCalculationId ?? 0,
    // Raw per-NRI verification states drive the Total Slot NRI/WI rollup donut
    // (SlotRollupVerificationStatus); pass them through unchanged.
    verifications: child.verifications ?? undefined,
    crmSubtractId: child.subtract ?? undefined,
    crmSubtractSalesforceId: child.id ?? undefined,
    crmTractSalesforceId: child.tractId ?? undefined,
    uat: child.unitAllocationTract?.name ?? undefined,
    uatNri: child.netRevenueInterest ?? undefined,
    uatWi: child.netWorkingInterest ?? undefined,
    subtractNetAcres: child.netAcres ?? undefined,
    subtractGrossAcres: child.grossAcres ?? undefined,
    quarterCall: child.quarterCall ?? undefined,
    interestType: child.interestType ?? undefined,
    partTractFactor: params.participationTractFactor ?? undefined,
    unitCoverage: params.unitCoverageThreshold ?? undefined,
    uatCoverage: params.uatCoverageThreshold ?? undefined,
    leaseRoyaltyRate: bundle.leaseRoyaltyRate ?? undefined,
    leaseRoyaltyType: bundle.leaseRoyaltyType ?? undefined,
    executive: bundle.executive ?? undefined,
    bonus: bundle.bonus ?? undefined,
    delay: bundle.delay ?? undefined,
    royalty: bundle.royalty ?? undefined,
    royaltyBurdens: bundle.royaltyBurdens ?? undefined,
    wi: bundle.wi ?? undefined,
    orri: bundle.orri ?? undefined,
    orriBurdens: bundle.orriBurdens ?? undefined,
  };
  // The BE child `id` (the subtract salesforce id) is the sub-table row key; it's
  // also surfaced as `crmSubtractSalesforceId` above for the CRM Subtract link.
  return attachRowMeta(subtract, { id: child.id });
}

/**
 * Maps an allocation child (`children[]` on an `assets` row) to the
 * `SlotAllocationRow` the asset expansion's slot subtable reads. Slot-grain — the
 * mock's `CHILD_COLUMNS_FOR_ASSETS` columns (Slot ID / Well Name / Unit /
 * Formation / Total Slot NRI / Total Slot WI / Well Status). Its nested subtract
 * `children` flatten onto `subtracts` so the Total Slot NRI/WI rollup popup works,
 * and `crmTractSalesforceId` + `slotId` drive the NRI-matrix popup.
 *
 * @param {AssetAllocationChildResponse} res - One raw asset allocation child.
 * @return {SlotAllocationRow} The FE slot row for the asset subtable.
 */
function mapAssetAllocationChild(
  res: AssetAllocationChildResponse
): SlotAllocationRow {
  const row: SlotAllocationRow = {
    slotId: res.slotId ?? undefined,
    slotIdHref: res.slotIdHref ?? undefined,
    wellName: res.wellName ?? undefined,
    uwi: res.uwi ?? undefined,
    unit: res.unit ?? undefined,
    unitTags: res.unitTags ?? undefined,
    formation: res.formationName ?? undefined,
    wellStatus: res.wellStatus ?? undefined,
    totalSlotNri: res.netRevenueInterest ?? undefined,
    totalSlotWi: res.netWorkingInterest ?? undefined,
    // This tract's share of the slot — what Interest Modeling prices. The two
    // above are the whole slot's, repeated on every tract row (INS-10673).
    tractNri: res.tractNetRevenueInterest ?? undefined,
    tractWi: res.tractNetWorkingInterest ?? undefined,
    crmTractSalesforceId: res.tractId ?? undefined,
    valued: res.valued ?? undefined,
    unvaluedReason: res.unvaluedReason ?? undefined,
    unvaluedReasons: res.unvaluedReasons ?? undefined,
    subtracts: (res.children ?? []).map(adaptSubtractChild),
  };
  return attachRowMeta(row, { id: res.id });
}

/**
 * Maps one (already-deduped) subtract child to the `AssetSubtractRow` the asset
 * expansion's subtract table reads (the legacy CRM Tracts subtract columns: Name /
 * Net Acres / Gross Acres / Depth Range / Title Quality / Title Risk Factor).
 * `subtract` (name) and `id` (salesforce id) drive the Name link; `depthRange`
 * arrives as the `{ startDepth, endDepth }` object and is formatted by `formatDepthRange`.
 *
 * @param {SubtractChildResponse} child - One raw subtract child.
 * @return {AssetSubtractRow} The CRM-subtract row for the asset subtable.
 */
function mapAssetSubtract(child: SubtractChildResponse): AssetSubtractRow {
  return {
    salesforceId: child.id ?? undefined,
    name: child.subtract ?? undefined,
    netAcres: child.netAcres ?? undefined,
    grossAcres: child.grossAcres ?? undefined,
    depthRange: formatDepthRange(child.depthRange),
    titleQuality: child.titleQuality ?? undefined,
    titleRiskFactor: child.titleRiskFactor ?? undefined,
  };
}

/**
 * The tract's distinct subtracts for the asset expansion's subtract table:
 * flattens every allocation child's nested subtract `children` and dedupes on the
 * subtract salesforce id (the same subtract repeats under each slot×tract
 * allocation). First occurrence wins; subtracts with no id are skipped (can't key).
 *
 * @param {AssetAllocationChildResponse[]} allocations - The asset's allocation children.
 * @return {AssetSubtractRow[]} The deduped CRM-subtract rows.
 */
function dedupeAssetSubtracts(
  allocations: AssetAllocationChildResponse[]
): AssetSubtractRow[] {
  const byId = new Map<string, AssetSubtractRow>();
  allocations.forEach((allocation) => {
    (allocation.children ?? []).forEach((child) => {
      if (child.id && !byId.has(child.id)) {
        byId.set(child.id, mapAssetSubtract(child));
      }
    });
  });
  return [...byId.values()];
}

/**
 * Derives the per-child map geometry (`ChildHighlight[]`) for a parent row, so the
 * expanded-row checkboxes can exclude a child's geometry from the pinned highlight.
 * The BE emits `mapElements` only at the parent grain, so each child's contribution
 * is reconstructed from its own scalar fields:
 *   - assets → the slot-allocation children: `slotId` (= well UWI) + `unit` name;
 *   - slots  → the tract-allocation children: the tract `crmTract` name (the well +
 *     unit are the slot's own geometry, so they live in the parent, not a child);
 *   - allocations → none (leaf rows; the subtract children are map-inert).
 * Each `id` equals the child subtable's `getRowId` (the BE child `id`), so a
 * checkbox toggle maps straight to a `ChildHighlight`. Pure (no React) for testing.
 *
 * @param {MasterDataView} view - The view whose children to derive.
 * @param {object} raw - The raw BE response row for that view.
 * @return {object[]} The per-child geometry; empty for the allocations view.
 */
export function deriveChildHighlights(
  view: MasterDataView,
  raw: AssetRowResponse | SlotRowResponse | SlotAllocationRowResponse
): ChildHighlight[] {
  if (view === 'assets') {
    return ((raw as AssetRowResponse).children ?? []).map((child) => ({
      id: String(child.id ?? ''),
      uwis: child.slotId ? [child.slotId] : [],
      names: child.unit ? [child.unit] : [],
    }));
  }
  if (view === 'slots') {
    return ((raw as SlotRowResponse).children ?? []).map((child) => ({
      id: String(child.id ?? ''),
      uwis: [],
      names: child.crmTract ? [child.crmTract] : [],
    }));
  }
  return [];
}

/**
 * Maps a raw master-data response (rows as untyped BE hashes) to the FE row
 * shapes, leaving the envelope (`totalCount`/`page`/`pageSize`/`hasMore`)
 * untouched — only `rows` is adapted, per the view.
 *
 * @param {MasterDataResponse<Record<string, unknown>>} raw - The fetched response with untyped rows.
 * @param {MasterDataView} view - The requested view (selects the row adapter).
 * @return {MasterDataResponse} The response with rows mapped to the FE shapes.
 */
export function adaptMasterDataResponse(
  raw: MasterDataResponse<Record<string, unknown>>,
  view: MasterDataView
): MasterDataResponse<AssetRow | SlotRow | SlotAllocationRow> {
  const rows = raw.rows.map((res): AssetRow | SlotRow | SlotAllocationRow => {
    if (view === 'assets') {
      // Asset `children` are allocation-grain (the slot allocations touching the
      // tract) → the asset expansion's slot subtable. Map them to `slotAllocations`
      // (each carries its own flattened subtracts for the NRI/WI rollup popup).
      // The CRM-subtract subtable comes from the same children's nested subtracts,
      // flattened + deduped on the subtract salesforce id.
      const assetRes = res as AssetRowResponse;
      const row = mapAssetRow(assetRes);
      const allocations = assetRes.children ?? [];
      row.slotAllocations = allocations.map(mapAssetAllocationChild);
      row.assetSubtracts = dedupeAssetSubtracts(allocations);
      // Per-child geometry for the slot-allocation subtable's exclusion checkboxes
      // (rides the row index signature like `mapElements`).
      (row as { childHighlights?: ChildHighlight[] }).childHighlights =
        deriveChildHighlights('assets', assetRes);
      return row;
    }
    if (view === 'allocations') {
      // Allocation `children` are subtract-grain → the subtract sub-table.
      const allocRes = res as SlotAllocationRowResponse;
      const row = mapAllocationRow(allocRes);
      row.subtracts = (allocRes.children ?? []).map(adaptSubtractChild);
      return row;
    }
    // slots: `children` are tract-grain (the tracts a slot spans), each with its
    // own nested subtract children. Map the tracts to `tractAllocations` (the
    // expansion sub-table) and flatten the nested subtracts onto `subtracts` (the
    // main-row NRI/WI rollup).
    const slotRes = res as SlotRowResponse;
    const row = mapSlotRow(slotRes);
    const tracts = slotRes.children ?? [];
    row.tractAllocations = tracts.map(mapTractChild);
    row.subtracts = tracts.flatMap((tract) =>
      (tract.children ?? []).map(adaptSubtractChild)
    );
    // Per-child geometry for the tract-allocation subtable's exclusion checkboxes.
    (row as { childHighlights?: ChildHighlight[] }).childHighlights =
      deriveChildHighlights('slots', slotRes);
    // A slot row has no top-level tractId (a slot can span tracts). Fall back to
    // its FIRST tract so the Total Slot NRI/WI matrix popup (gated on
    // `crmTractSalesforceId`) renders — matching legacy's slot-row builder, which
    // sets `crmTractSalesforceId: firstTract?.salesforceId` (utils/misc.ts).
    row.crmTractSalesforceId =
      row.crmTractSalesforceId ?? tracts[0]?.tractId ?? undefined;
    return row;
  });
  return { ...raw, rows };
}

/**
 * Builds the relative URL of a package-scoped map tile's TileJSON descriptor
 * (`GET /packages/:packageId/maps/sources/:id`). This is the value passed to a
 * react-map-gl `<Source url={…}>`: mapbox-gl GETs it, reads the `tiles` template
 * + `minzoom`, and fetches the actual `{x}-{y}-{z}` tiles itself. Relative on
 * purpose — mapbox resolves it against the document, matching the existing map
 * layers (which feed `maps_source_path(...)` straight into `<Source url>`).
 *
 * When a `calculationId` is supplied it rides along as a query param so the BE
 * resolves the source in CALC mode — scoping the tiles to the calc's frozen blob
 * slots (the same universe the master_data table reads), rather than the live
 * package baseline. Omitted ⇒ package mode (the baseline "all package wells").
 *
 * @param {number} packageId - The package the tiles are scoped to.
 * @param {PackageMapSource} source - The tile source id (wells_v2 / assets / development_areas).
 * @param {number | null} [calculationId] - The eval-calc id (calc mode); omit / null for package mode.
 * @param {number | null} [typeCurveScenarioId] - Type Curve Scenario → `?typeCurveScenarioId=`;
 *   read only by the `insights_ltd_slots` source, which resolves the tile's filterSet against it.
 * @return {string} The relative TileJSON descriptor URL.
 */
/**
 * A raw `<Source tiles={[…]}>` template must be ABSOLUTE — mapbox-gl resolves it itself rather than
 * through the page, so a root-relative path fails. Only the TileJSON `url` form tolerates relative.
 *
 * @param {string} path - The root-relative tile path, including its `{x}-{y}-{z}` template.
 * @return {string} The same path against the current origin.
 */
export const absoluteTileUrl = (path: string): string =>
  `${window.location.origin}${path}`;

/**
 * The DIRECT `{x}-{y}-{z}` template for the LTD-stick tiles in SCENARIO mode — the Manage Type
 * Curves modal's sticks layer.
 *
 * Deliberately not the TileJSON descriptor (`/maps/sources/insights_ltd_slots`): that endpoint bakes
 * a filterSet TOKEN, and a token is minted per override set, so trying five curves in the modal
 * would resolve five times and write ~2,352 member rows each. The tile endpoint takes the scenario
 * directly and serves a cheap superset instead, which the caller filters to the row's own slot ids.
 *
 * @param {number} packageId - The package the tiles are scoped to.
 * @param {number | null} scenarioId - The effective scenario; null yields an empty layer.
 * @return {string} The absolute tile-template URL.
 */
export function ltdSlotsTileUrl(
  packageId: number,
  scenarioId: number | null
): string {
  const query = scenarioId === null ? '' : `?typeCurveScenarioId=${scenarioId}`;
  return absoluteTileUrl(
    `/packages/${packageId}/maps/tiles/insights_ltd_slots/{x}-{y}-{z}${query}`
  );
}

export function packageMapSourceUrl(
  packageId: number,
  source: PackageMapSource,
  calculationId?: number | null,
  typeCurveScenarioId?: number | null,
  typeCurveOverrides?: readonly TypeCurveOverride[],
  inventoryPackageOverrides?: readonly InventoryPackageOverride[]
): string {
  const path = `/packages/${packageId}/maps/sources/${source}`;
  const query = new URLSearchParams();
  if (typeof calculationId === 'number') {
    query.set('calculationId', String(calculationId));
  }
  // Every token-baking source reads this (the sources view lists them): it decides WHICH
  // places are members, so it belongs in the URL — the token baked into the returned
  // TileJSON is resolved against this scenario (INS-10669).
  if (typeof typeCurveScenarioId === 'number') {
    query.set('typeCurveScenarioId', String(typeCurveScenarioId));
  }
  // The TC Manager's choices, for the same reason: the filterSet token minted by the sources
  // endpoint is hashed over them (FilterSet::Token), so a tile URL that omits them resolves
  // membership against the DEFAULT curves. It belongs on THIS call rather than the tile call —
  // the tile joins the members the token already decided, and takes no override of its own.
  // `packages/maps/sources/show.json.jbuilder` decodes it with TypeCurveOverrides.decode.
  const encoded = encodeTypeCurveOverrides(typeCurveOverrides ?? []);
  if (encoded) {
    query.set('tcOverrides', encoded);
  }
  // The spacing half rides the same way — the token is hashed over it too (INS-10669).
  // Decoded by `packages/maps/sources/show.json.jbuilder` with InventoryPackageOverrides.decode.
  const encodedPackages = encodeInventoryPackageOverrides(
    inventoryPackageOverrides ?? []
  );
  if (encodedPackages) {
    query.set('ipOverrides', encodedPackages);
  }
  const suffix = query.toString();
  return suffix ? `${path}?${suffix}` : path;
}

// ─── Visibility ∩ pin: scoped-layer filters ───
// The scoped layers narrow on two AND-ed signals: the page-filter VISIBILITY (the
// `map_elements` ids for the active dataset filters + spatial scope) and the PIN (a
// located row's related geometry). Each builder takes both sets and intersects them
// under `['all']`; neither set → no membership clause → every feature draws. Pure so
// they're unit-testable; the per-layer hooks read `useMapVisibility` +
// `mapHighlightAtom` and pass both here.
//
// NOTE: legacy filter syntax (`['==', 'type', 'shape']`, `['in', 'name', ...vals]`)
// — NOT expression syntax (`['==', ['get', 'type'], 'shape']`). Expression filters
// disable mapbox-gl's tile-level feature culling, which makes zoom janky on the
// always-mounted polygon layers; the legacy form keeps the prior smooth behavior
// and matches the codebase convention (`components/map/development-areas-layer.tsx`).

/**
 * Polygon-layer base filter: draw only the polygon `shape` features (the Tract /
 * Unit tiles also emit `centroid` label points we never draw). A module constant
 * so the unpinned filter is a STABLE reference at the source — the layers re-render
 * during map interaction, and a fresh array each render makes react-map-gl re-apply
 * the filter and jank zoom (the per-layer `useMemo` also guards this, but a stable
 * source keeps the no-jank invariant from resting solely on the memo dependency).
 */
export const POLYGON_SHAPE_ONLY: Expression = ['==', 'type', 'shape'];

/**
 * The Slot layer's no-narrow filter — `['all']` matches every well. A module
 * constant so the unfiltered/unpinned filter is a STABLE reference at the source,
 * mirroring `POLYGON_SHAPE_ONLY` (keeps the no-jank invariant from resting solely on
 * the per-layer `useMemo`).
 */
const SLOT_SHOW_ALL: Expression = ['all'];

/** Stable module ref for "nothing narrows the LTD layer" (mirrors `SLOT_SHOW_ALL`). */
const LTD_SHOW_ALL: Expression = ['all'];

/**
 * Mapbox `filter` for the Slot layer (`wells_v2`): membership on the well `uwi`,
 * intersecting the two narrowing signals — the page-filter **visibility**
 * (`map_elements`) and the **pin** highlight. Each non-null signal contributes one
 * legacy `['in', 'uwi', …]` clause, AND-ed under `['all']`, so a well draws only if
 * it's in BOTH sets. Neither set → `['all']` (a no-op matching every well). An empty
 * set's `['in', 'uwi']` matches nothing → the layer draws nothing (the intended
 * "filtered out" state).
 *
 * MUST return a valid filter array, never `undefined`: react-map-gl spreads the
 * `filter` prop straight into `map.addLayer`, and mapbox rejects `filter: undefined`
 * with a validation error — which silently prevents the layer from ever attaching.
 *
 * @param {MapHighlight} visibility - The page-filter visibility set, or `null` when nothing narrows.
 * @param {MapHighlight} pin - The pinned-row set, or `null` when nothing is pinned.
 * @return {Expression} The slot-layer filter (`['all']` = show every well).
 */
export function slotHighlightFilter(
  visibility: MapHighlight,
  pin: MapHighlight
): Expression {
  const clauses: Expression[] = [];
  if (visibility) {
    clauses.push(['in', 'uwi', ...visibility.uwis]);
  }
  if (pin) {
    clauses.push(['in', 'uwi', ...pin.uwis]);
  }
  // Stable module ref when nothing narrows (mirrors the polygon builder).
  return clauses.length === 0 ? SLOT_SHOW_ALL : ['all', ...clauses];
}

/**
 * The LTD layer's Mapbox `filter` — the page-filter VISIBILITY intersected with the PIN, by
 * `slot_id` (the composite `{unit}_{formation}_{slotNumber}` the tile emits).
 *
 * Keys on `slotIds`, NOT `uwis` or `names`: a Type Curve LTD place has no UWI, so a uwi clause
 * would match nothing and hide every stick, while a `name` clause would match its UNIT and so
 * light up every sibling place in it. `slotIds` is the only identifier that selects the one
 * place the user pinned.
 *
 * Legacy filter syntax, like the sibling builders — expression syntax disables mapbox-gl's
 * tile-level feature culling and makes zoom janky on an always-mounted layer.
 *
 * @param {MapHighlight} visibility - The active dataset filters + spatial scope, or null.
 * @param {MapHighlight} pin - The located row's related geometry, or null.
 * @return {Expression} The membership filter; a stable module ref when neither set narrows.
 */
export function ltdSlotHighlightFilter(
  visibility: MapHighlight,
  pin: MapHighlight
): Expression {
  const clauses: Expression[] = [];
  if (visibility) {
    clauses.push(['in', 'slot_id', ...visibility.slotIds]);
  }
  if (pin) {
    clauses.push(['in', 'slot_id', ...pin.slotIds]);
  }
  return clauses.length === 0 ? LTD_SHOW_ALL : ['all', ...clauses];
}

/**
 * The Slot layer's two gated lateral-line filters, derived from `base`, encoding
 * the eval3-new single-lateral hierarchy **survey > stick > EPS** (INS-10376 Bug 7).
 * Exactly one lateral draws per well; the survey layer needs no gate (its
 * source-layer only holds survey-having wells).
 *
 * - `stick` draws when the well has no survey — the preferred lateral here.
 * - `eps` is the last-resort fallback: no survey AND no stick. It should basically
 *   never render (sandbox data has no such wells), so an EPS line signals a well
 *   missing its stick geometry — a data problem, not a code one. Keys on stick
 *   *presence* (`has_stick`), matching the presence-based flag family; a degenerate
 *   stick would suppress the EPS, but the data has none (Bug 7 UAT check).
 *
 * `!= true` (not the sibling layers' `== false`) matches this view's Bug 2
 * convention; equivalent here since the tile emits the flags as `CASE … ELSE FALSE`.
 * Pure fn so the gate ordering is unit-tested and can't be silently re-swapped.
 *
 * @param {Expression} base - The visibility ∩ pin membership filter (from `slotHighlightFilter`).
 * @return {{ stick: Expression, eps: Expression }} The two gated lateral-line filters.
 */
export function slotLateralFilters(base: Expression): {
  stick: Expression;
  eps: Expression;
} {
  // eps is stick's gate narrowed further (also no stick) — the fallback ladder.
  const stick: Expression = ['all', base, ['!=', 'has_survey', true]];
  const eps: Expression = ['all', stick, ['!=', 'has_stick', true]];
  return { stick, eps };
}

/**
 * Mapbox `filter` for the polygon layers (Tract `assets`, Unit `development_areas`):
 * always restricts to the polygon `shape` features (the tiles also emit `centroid`
 * label points we don't draw), then intersects the two narrowing signals — the
 * page-filter **visibility** and the **pin** — each contributing one legacy
 * `['in', 'name', …]` clause AND-ed under `['all']`. Neither set → just the shape
 * filter (all polygons draw). `names` bundles tract + unit (+ subtract) names — each
 * layer matches only its own `name`, so cross-layer entries are inert. Legacy filter
 * syntax (see the note above).
 *
 * @param {MapHighlight} visibility - The page-filter visibility set, or `null` when nothing narrows.
 * @param {MapHighlight} pin - The pinned-row set, or `null` when nothing is pinned.
 * @return {Expression} The polygon-layer filter (shape-only, plus name membership per active set).
 */
export function polygonHighlightFilter(
  visibility: MapHighlight,
  pin: MapHighlight
): Expression {
  const clauses: Expression[] = [POLYGON_SHAPE_ONLY];
  if (visibility) {
    clauses.push(['in', 'name', ...visibility.names]);
  }
  if (pin) {
    clauses.push(['in', 'name', ...pin.names]);
  }
  // Just the shape filter (a stable module ref) when neither set narrows.
  return clauses.length === 1 ? POLYGON_SHAPE_ONLY : ['all', ...clauses];
}

// ─── Dataset filters (P2) ───
// Pure helpers for the filter UI. NOTE: the AppliedFilter→ServerFilter mapping
// is NOT here — it's inlined in `serverFiltersAtom` (atoms.ts) because this
// module imports `locatedRowAtom` from atoms.ts, so importing a util back would
// make atoms ↔ utils a cycle. See the deferred ledger.

/** En-dash range separator for a two-bound summary (`8,000 – 12,000`). */
const RANGE_SEPARATOR = ' – ';

/** Card/summary display granularity for a date bound (e.g. "Dec 2025"). */
const DATE_SUMMARY_FORMAT = 'MMM YYYY';

/**
 * Domain acronyms `startCase` would otherwise title-case (`crmTract` → "Crm
 * Tract"). Keyed by the `startCase`-cased word → its all-caps form, applied
 * per-word so the label reads "CRM Tract" / "DCA Scenario". The eval3 filter
 * catalog only embeds CRM (`crmTract`/`crmAccount`) and DCA (`dcaScenario`); add
 * a row here when a new attribute carries another acronym.
 */
const LABEL_ACRONYMS: Record<string, string> = {
  Afe: 'AFE',
  Crm: 'CRM',
  Dca: 'DCA',
  Dps: 'DPS',
  Id: 'ID',
  Ngl: 'NGL',
  Nma: 'NMA',
  Npv: 'NPV',
  Nri: 'NRI',
  Uwi: 'UWI',
  Wi: 'WI',
};

/**
 * Attributes whose label is not their humanized key, so the filter reads the same
 * as its table column.
 */
const ATTRIBUTE_LABELS: Record<string, string> = {
  volumeSourceId: 'Volume Source',
};

/**
 * Humanizes a camelCase attribute key into a display label (`wellStatus` → "Well
 * Status", `crmAccount` → "CRM Account"). `startCase` does the title-casing; a
 * per-word acronym pass then restores the domain acronyms it would otherwise
 * lowercase (CRM / DCA). The `filter_options` catalog is keyed by the raw
 * attribute vocabulary with no labels, so the panel derives one for the modal
 * list + the filter / color cards.
 *
 * @param {string} attribute - The camelCase attribute key.
 * @return {string} The humanized display label, with domain acronyms preserved.
 */
export function attributeLabel(attribute: string): string {
  const label = ATTRIBUTE_LABELS[attribute];
  if (label) {
    return label;
  }
  return startCase(attribute)
    .split(' ')
    .map((word) => LABEL_ACRONYMS[word] ?? word)
    .join(' ');
}

/**
 * Formats one range bound for a card summary: a numeric bound via `formatNumber`
 * (commas, up to 8 dp, unit appended separately), a date bound via moment
 * (`MMM YYYY`).
 *
 * @param {number | string} bound - The bound value (number for numericRange, ISO string for dateRange).
 * @param {FilterKind} kind - The range kind (selects numeric vs date formatting).
 * @return {string} The formatted bound.
 */
function formatRangeBound(bound: number | string, kind: FilterKind): string {
  if (kind === 'dateRange') {
    return moment(String(bound)).format(DATE_SUMMARY_FORMAT);
  }
  return formatNumber({
    value: Number(bound),
    minPrecision: 0,
    maxPrecision: 8,
    includeCommas: true,
    fallback: '',
  });
}

/**
 * Kind-aware min–max summary for a range filter card, reflecting open-ended
 * selections: both bounds → `8,000 – 12,000 ft`; lower only → `≥ 8,000 ft`; upper
 * only → `≤ Dec 2025`. The optional `unit` is appended once at the end (numeric
 * ranges only in practice). Returns `''` when neither bound is set (the Apply
 * gating prevents an empty range, so this is only a defensive fallback).
 *
 * @param {FilterRange} range - The inclusive, possibly open-ended range.
 * @param {FilterKind} kind - The range kind (numericRange / dateRange).
 * @param {string} [unit] - Optional unit suffix (e.g. "ft"); omitted ⇒ no suffix.
 * @return {string} The formatted range summary.
 */
export function formatRangeSummary(
  range: FilterRange,
  kind: FilterKind,
  unit?: string
): string {
  const suffix = unit ? ` ${unit}` : '';
  const min =
    range.min !== undefined && range.min !== ''
      ? formatRangeBound(range.min, kind)
      : null;
  const max =
    range.max !== undefined && range.max !== ''
      ? formatRangeBound(range.max, kind)
      : null;
  if (min !== null && max !== null) {
    return `${min}${RANGE_SEPARATOR}${max}${suffix}`;
  }
  if (min !== null) {
    return `≥ ${min}${suffix}`;
  }
  if (max !== null) {
    return `≤ ${max}${suffix}`;
  }
  return '';
}

/** A categorical filter card's value tags: the first `max`, plus an overflow count. */
export interface ValueTags {
  /** The values to render as tags (≤ `max`). */
  tags: string[];
  /** How many values are hidden beyond `max` (drives the "+N more" tag); 0 = none. */
  overflow: number;
}

/**
 * Splits a categorical filter's selected values into the first `max` (rendered as
 * tags) and an overflow count (the "+N more" tag), per the mock's card body.
 *
 * @param {string[]} values - The selected values.
 * @param {number} [max] - How many tags to show before collapsing the rest (default 3).
 * @return {ValueTags} The shown tags and the hidden-count.
 */
export function valuesToTags(values: string[], max = 3): ValueTags {
  return {
    tags: values.slice(0, max),
    overflow: Math.max(0, values.length - max),
  };
}

/**
 * Merges the attribute-filter modal's Apply result into a dataset's existing list
 * — the shared glue behind both `datasetFiltersAtom` (filters) and `colorRulesAtom`
 * (color rules). Scoped to one dataset: entries of OTHER datasets pass through
 * untouched and in place (the filter list is one array across wells/units/assets,
 * so the merge keys on **(dataset, attribute)**). Within the scope dataset: an
 * existing entry whose attribute is in `selections` is updated in place (via
 * `toEntry(selection, existingEntry)`, so the caller keeps its id / color order);
 * a selection with no existing entry is appended last; an attribute in
 * `removedAttributes` (cleared to empty in the modal) is dropped. Pure — id
 * generation + field stamping live in the caller's `toEntry`.
 *
 * @template TEntry - One list entry (`AppliedFilter` or `ColorRule`); must carry `attribute` + `dataset`.
 * @param {TEntry[]} currentEntries - The current full list (all datasets).
 * @param {DatasetName} scopeDataset - The dataset the modal was applied for (the merge scope).
 * @param {FilterSelection[]} selections - The modal's touched attributes (≥1 value) → create/update.
 * @param {string[]} removedAttributes - Attributes cleared to empty in the modal → delete.
 * @param {Function} toEntry - Builds/updates an entry from a selection (passed the existing entry on update).
 * @return {TEntry[]} The next list.
 */
export function mergeByAttribute<
  TEntry extends { attribute: string; dataset: DatasetName },
>(
  currentEntries: TEntry[],
  scopeDataset: DatasetName,
  selections: FilterSelection[],
  removedAttributes: string[],
  toEntry: (selection: FilterSelection, existingEntry?: TEntry) => TEntry
): TEntry[] {
  const removedAttributeSet = new Set(removedAttributes);
  const selectionByAttribute = new Map(
    selections.map((selection) => [selection.attribute, selection])
  );
  // Attributes already present in the scope dataset, so the second pass only
  // appends selections that don't yet have an entry.
  const existingAttributes = new Set<string>();

  const nextEntries: TEntry[] = [];
  currentEntries.forEach((entry) => {
    // Other datasets are out of scope — keep their entries exactly as-is.
    if (entry.dataset !== scopeDataset) {
      nextEntries.push(entry);
      return;
    }
    // In scope: a removed attribute drops out; a touched one updates in place
    // (keeping its id / order via toEntry's existingEntry arg); else unchanged.
    if (removedAttributeSet.has(entry.attribute)) {
      return;
    }
    existingAttributes.add(entry.attribute);
    const matchingSelection = selectionByAttribute.get(entry.attribute);
    nextEntries.push(
      matchingSelection ? toEntry(matchingSelection, entry) : entry
    );
  });

  // New in-scope selections (no existing entry for that attribute) append last.
  selections.forEach((selection) => {
    if (
      !existingAttributes.has(selection.attribute) &&
      !removedAttributeSet.has(selection.attribute)
    ) {
      nextEntries.push(toEntry(selection));
    }
  });

  return nextEntries;
}

// ─── Color rules (P2) ───
// Pure helpers turning a dataset's color rules into the per-distinct-color
// map_elements requests (`toColorGroups`), the resolved ids into a Mapbox paint
// expression (`buildColorExpression`), and the stitch between them
// (`mergeColorElements`). Color is FE-only styling — never sent as a filter; these
// resolve ids via map_elements and paint them, layered over the visibility filter.

/**
 * Expands a dataset's color rules into one resolution group per DISTINCT COLOR
 * within each rule — the unit `useColorElements` runs one `map_elements` call for.
 * A categorical rule groups its selected values by their assigned color (values
 * sharing a color OR into one group's `selectedValues`); a value with no assigned
 * color is skipped (nothing to paint). A range rule yields a single group for its
 * `rangeColor` (skipped when the color or range is unset). Each group's
 * `serverFilter` is single-attribute + value-only, tagged with the rule's dataset
 * `catalog` so `map_elements` scopes the right element types. Rules are never
 * combined across attributes (a server filter is single-attribute, and multiple
 * server filters AND rather than union). The caller passes only the map-painting
 * rules (enabled ∧ targeting the map). Pure — unit-testable.
 *
 * @param {ColorRule[]} rules - The dataset's map-painting color rules, in listing order.
 * @return {ColorGroup[]} One group per (rule × distinct color), in rule-then-color order.
 */
export function toColorGroups(rules: ColorRule[]): ColorGroup[] {
  const groups: ColorGroup[] = [];
  rules.forEach((rule) => {
    if (rule.kind === 'categorical') {
      // Group this rule's selected values by their assigned color, preserving the
      // order colors are first seen so the output is deterministic.
      const valuesByColor = new Map<string, string[]>();
      (rule.selectedValues ?? []).forEach((value) => {
        const color = rule.valueColors?.[value];
        if (!color) {
          return;
        }
        const existing = valuesByColor.get(color);
        if (existing) {
          existing.push(value);
        } else {
          valuesByColor.set(color, [value]);
        }
      });
      valuesByColor.forEach((selectedValues, color) => {
        groups.push({
          color,
          serverFilter: {
            attribute: rule.attribute,
            catalog: rule.dataset,
            selectedValues,
          },
        });
      });
      return;
    }
    // Range rule (numeric / date): one color for the whole range.
    if (rule.rangeColor && rule.range) {
      groups.push({
        color: rule.rangeColor,
        serverFilter: {
          attribute: rule.attribute,
          catalog: rule.dataset,
          range: rule.range,
        },
      });
    }
  });
  return groups;
}

/**
 * Stitches the `toColorGroups` groups with their `map_elements` results into the
 * ordered `ColorElements[]` the layers paint from. Positional: `results[i]` is the
 * response for `groups[i]` (the `useQueries` fan-out preserves order), so the output
 * keeps rule-then-color order — which is what makes `buildColorExpression`'s
 * last-listed-wins precedence match the rule listing. A still-loading / errored /
 * empty result contributes empty id arrays (that color simply paints nothing until
 * its ids arrive). Pure — unit-testable.
 *
 * @param {ColorGroup[]} groups - The color groups (from `toColorGroups`).
 * @param {Array} results - The per-group map_elements results (`{ data? }`), in the same order.
 * @return {ColorElements[]} The color → resolved ids list, in group order.
 */
export function mergeColorElements(
  groups: ColorGroup[],
  results: { data?: MapElements }[]
): ColorElements[] {
  return groups.map((group, index) => ({
    color: group.color,
    uwis: results[index]?.data?.uwis ?? [],
    names: results[index]?.data?.names ?? [],
  }));
}

/**
 * Builds the Mapbox `match` paint expression coloring features by the resolved
 * color groups, keyed on the layer's id property (`'uwi'` for the Slot layer,
 * `'name'` for the Tract / Unit polygon layers). Features in no group fall back to
 * `defaultColor` (the layer's flat color). Precedence is LAST-LISTED-WINS: the
 * groups arrive in rule-listing order, and a feature appearing in more than one is
 * painted by the LAST — so each id maps to exactly one color (Mapbox `match` labels
 * must be unique). Ids are grouped into array labels for a compact expression (one
 * branch per color, not per id), mirroring the type-curves `buildColorExpr`. Returns
 * the bare `defaultColor` when nothing resolves (a `match` needs ≥1 branch). Pure —
 * unit-testable.
 *
 * @param {ColorElements[]} colorElements - The resolved color groups, in rule-listing order.
 * @param {string} key - The feature id property to match on (`'uwi'` or `'name'`).
 * @param {string} defaultColor - The fallback for unmatched features (the layer's flat color).
 * @return {string | Expression} The `match` expression, or `defaultColor` when nothing resolves.
 */
export function buildColorExpression(
  colorElements: ColorElements[],
  key: 'uwi' | 'name',
  defaultColor: string
): string | Expression {
  // Last-listed wins: walk the groups in order, mapping each id to its color so a
  // later group overwrites an earlier one — leaving each id with exactly one color
  // (Mapbox `match` rejects duplicate labels).
  const idToColor = new Map<string, string>();
  colorElements.forEach((element) => {
    const ids = key === 'uwi' ? element.uwis : element.names;
    ids.forEach((id) => idToColor.set(id, element.color));
  });
  if (idToColor.size === 0) {
    return defaultColor;
  }

  // Invert to color → ids (first-seen color order) for the compact array-label form.
  const idsByColor = new Map<string, string[]>();
  idToColor.forEach((color, id) => {
    const existing = idsByColor.get(color);
    if (existing) {
      existing.push(id);
    } else {
      idsByColor.set(color, [id]);
    }
  });

  const branches: (string | string[])[] = [];
  idsByColor.forEach((ids, color) => {
    branches.push(ids.length === 1 ? ids[0] : ids, color);
  });

  return [
    'match',
    ['get', key],
    ...branches,
    defaultColor,
  ] as unknown as Expression;
}

/**
 * Display-only color rules for the sidebar legend (INS-10376 Bug 13): trim each
 * categorical rule's values to those still present in the fully-narrowed `colorBy`
 * catalog and drop a rule whose values are all gone — so a color assigned to a value
 * that's been filtered out disappears from the legend, while the stored rule keeps
 * every value (clearing the filter restores them). Range rules pass through unchanged
 * (no enumerable values); a categorical attribute absent from the catalog means every
 * one of its values was filtered out, so its rule drops. Under the cascade EVERY
 * attribute (entity + descriptive) narrows in `colorBy`, so any color can hide.
 * Callers pass the loaded per-dataset `colorBy` catalog (guard loading).
 *
 * @param {ColorRule[]} rules - The dataset's stored color rules.
 * @param {FilterCatalog | undefined} catalog - The dataset's fully-narrowed colorBy catalog.
 * @return {ColorRule[]} The rules to render, with filtered-out values removed.
 */
export function visibleColorRules(
  rules: ColorRule[],
  catalog: FilterCatalog | undefined
): ColorRule[] {
  return rules
    .map((rule) => {
      if (rule.kind !== 'categorical') {
        return rule;
      }
      const option = catalog?.[rule.attribute];
      const present = new Set(
        option?.type === 'categorical' ? option.values : []
      );
      const selectedValues = (rule.selectedValues ?? []).filter((value) =>
        present.has(value)
      );
      const valueColors = Object.fromEntries(
        Object.entries(rule.valueColors ?? {}).filter(([value]) =>
          present.has(value)
        )
      );
      return { ...rule, selectedValues, valueColors };
    })
    .filter(
      (rule) =>
        rule.kind !== 'categorical' || (rule.selectedValues?.length ?? 0) > 0
    );
}

/**
 * Deselect safety for the Add-Filter picker (INS-10376 Bug 13): a filter's own
 * cascade option is narrowed by the filters ABOVE it, so a value the user already
 * selected could be narrowed out and vanish from its picker — leaving it stuck on.
 * Union the attribute's currently-selected values back into a categorical option so
 * they stay visible (and therefore deselectable). This never offers a NEW value that
 * would empty the set — the cascade already excludes those; it only re-adds values
 * the user themselves picked. Non-categorical options and empty selections pass
 * through unchanged.
 *
 * The union is CASE-INSENSITIVE: the BE's categorical() collapses case-variants to a
 * single canonical casing (e.g. "Producing"/"producing" → whichever is most common),
 * so a stale selection persisted in the losing casing must NOT be re-added alongside
 * the catalog's casing (that would duplicate the picker entry, and unchecking one
 * would leave the other). A selection already represented (case-insensitively) by a
 * catalog value is dropped — the catalog's casing wins.
 *
 * @param {FilterOption} option - The attribute's cascade option.
 * @param {string[] | undefined} selected - The attribute's currently-selected values.
 * @return {FilterOption} The option with selected values ensured present.
 */
export function withSelectedValuesVisible(
  option: FilterOption,
  selected: string[] | undefined
): FilterOption {
  if (option.type !== 'categorical' || !selected?.length) {
    return option;
  }
  const present = new Set(option.values.map((value) => value.toLowerCase()));
  const missing = selected.filter((value) => !present.has(value.toLowerCase()));
  if (missing.length === 0) {
    return option;
  }
  return { ...option, values: [...option.values, ...missing].sort() };
}

// ─── P4 · Cases & Per-Area Overrides ───
// Pure helpers + constants for the Case-management surface: the domain row
// vocabulary (order / labels / options endpoints), the coverage health bands, the
// client-side coverage partitioning (one threshold=0 fetch, the slider
// re-partitions locally), and the overrides pruning `buildCalculateRequest`
// applies before send.

/** Display label per case domain (the area rows + the scenario picker title). */
export const CASE_DOMAIN_LABELS: Record<CaseDomain, string> = {
  development_pace: 'Development Pace',
  taxes: 'Taxes',
  differentials: 'Differentials',
  discount_rates: 'Discount Rates',
  expenses: 'Expenses',
};

/**
 * Scenario-options query endpoint per domain (`POST`). No leading slash — the
 * `api` (ky) instance sets `prefixUrl`, so paths must be relative.
 */
export const CASE_DOMAIN_ENDPOINTS: Record<CaseDomain, string> = {
  development_pace: 'queries/development_pace_scenarios',
  taxes: 'queries/tax_scenarios',
  differentials: 'queries/differential_scenarios',
  discount_rates: 'queries/discount_rate_scenarios',
  expenses: 'queries/expense_scenarios',
};

/** A coverage bar's health band — the PRD color language (P4 FR#4). */
export type CoverageBand = 'green' | 'amber' | 'red';

/**
 * The coverage health band for a coverage percent: green ≥ 90, amber 60–89,
 * red < 60 (P4 FR#4). Applied to the coverage bars in both the coverage tile and
 * the case-picker tiles (the band maps to an scss class at the callsite).
 *
 * @param {number} pct - The coverage percent (0–100).
 * @return {CoverageBand} The health band.
 */
export function coverageBand(pct: number): CoverageBand {
  if (pct >= 90) {
    return 'green';
  }
  if (pct >= 60) {
    return 'amber';
  }
  return 'red';
}

/**
 * Formats a coverage percent for display (up to one decimal, the BE precision).
 * Shared by the case-picker tiles and the coverage tile so "[X]%" renders
 * identically in both (no `%` suffix — the callsites append it).
 *
 * @param {number} pct - The coverage percent (0–100).
 * @return {string} The formatted percent.
 */
export const formatCoveragePct = (pct: number): string =>
  formatNumber({
    value: pct,
    minPrecision: 0,
    maxPrecision: 1,
    includeCommas: false,
    fallback: EMPTY,
  });

/**
 * Whether a coverage row covers the WHOLE package — the missing COUNTS are the
 * single source of truth. `coveragePct` is deliberately NOT consulted: it's
 * one-decimal rounded, so 99.96 arrives as 100 while rows are still missing —
 * gating on it would hide a real shortfall right before a Calculate. Drives the
 * all-covered vs missing-breakdown row on both the picker tiles and the
 * coverage tile, and the partial-coverage warning's visibility (whose text
 * handles the rounds-to-zero shortfall case).
 *
 * @param {EvaluationCaseCoverage} row - The case's coverage row.
 * @return {boolean} True when nothing in the package is uncovered.
 */
export function isFullyCovered(row: EvaluationCaseCoverage): boolean {
  // Nothing missing, but nothing covered either: not "fully covered".
  return (
    row.nothingToValue !== true &&
    row.missing.assets + row.missing.units + row.missing.slots === 0
  );
}

/**
 * Splits coverage rows by the Min-coverage slider threshold, client-side — the
 * picker fetches `coverage_list` ONCE with `threshold=0` (everything lands in
 * `aboveThreshold`) and re-partitions locally on every slider move, so there's no
 * refetch per tick and the picker state trivially survives drill-in round-trips.
 * A row at exactly the threshold counts as above (the slider reads "Min
 * coverage"). A row with nothing left to value is neither covered nor short, so it is always
 * above. Input order is preserved (the BE list is coverage-desc sorted).
 *
 * @param {EvaluationCaseCoverage[]} cases - The full coverage list (above + below concat).
 * @param {number} threshold - The slider's minimum coverage percent (0–100).
 * @return {{ above: EvaluationCaseCoverage[], below: EvaluationCaseCoverage[] }} The partitioned rows.
 */
export function partitionCoverage(
  cases: EvaluationCaseCoverage[],
  threshold: number
): { above: EvaluationCaseCoverage[]; below: EvaluationCaseCoverage[] } {
  const [above, below] = partition(
    cases,
    (row) => row.nothingToValue === true || row.coveragePct >= threshold
  );
  return { above, below };
}

/**
 * A run's stored overrides without the area-keyed map an earlier, area-mode run saved. That map
 * no longer applies (the server resolves only the case's scenarios and `{ domain: id }`
 * overrides), so a reopened run seeds without it and Calculate never sends it.
 *
 * @param {EvaluationCaseOverrides} overrides - The run's stored overrides.
 * @return {EvaluationCaseOverrides} The same overrides, with no `areas`.
 */
export function withoutAreaOverrides(
  overrides: EvaluationCaseOverrides
): EvaluationCaseOverrides {
  const { areas, ...rest } = overrides;
  return rest;
}

/**
 * The deck pick a reopened run seeds. A case run's own pick is its override (`priceDeckId`, else
 * `price_deck_id`, as the server reads them); with none it ran on the case's deck, so it seeds no
 * pick.
 * Its `configurations.price_deck_id` holds only a deck the request named, but a rerun from before
 * 49c6057020 kept an earlier pick there while pricing on the case's deck, so it isn't read.
 * A run with no case keeps its deck on `configurations.price_deck_id`, as before.
 *
 * @param {object} configurations - The run's stored configurations.
 * @param {number | null} [configurations.evaluation_case_id] - The run's case, if any.
 * @param {EvaluationCaseOverrides} [configurations.evaluation_case_overrides] - Its overrides.
 * @param {number | null} [configurations.price_deck_id] - The deck merged onto the run.
 * @return {number | null} The deck pick to seed, or null for the case's own deck.
 */
export function seedPriceDeckId(configurations: {
  evaluation_case_id?: number | null;
  evaluation_case_overrides?: EvaluationCaseOverrides | null;
  price_deck_id?: number | null;
}): number | null {
  if (
    configurations.evaluation_case_id === null ||
    configurations.evaluation_case_id === undefined
  ) {
    return configurations.price_deck_id ?? null;
  }
  const overrides = (configurations.evaluation_case_overrides ?? {}) as Record<
    string,
    unknown
  >;
  // As the server reads it (EvaluationCase::CaseScenarios.price_deck_override_id): priceDeckId
  // before price_deck_id, a blank one being no pick, and a digit string as its id.
  return deckId(overrides.priceDeckId) ?? deckId(overrides.price_deck_id);
}

/**
 * A deck id as an override holds it: a number, or a string of digits; anything else is none.
 *
 * @param {unknown} value - The override's value.
 * @return {number | null} The id.
 */
function deckId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return null;
}

/**
 * Whether a run's stored overrides carry any per-area override (an area with at least one
 * domain picked), which a reopened run no longer applies. The server's "Dropped the per-area
 * overrides" activity line reads it the same way (`area_overrides?`). Stored JSON can hold an
 * area with `null` or a non-object, which overrides nothing.
 *
 * @param {EvaluationCaseOverrides | undefined} overrides - The run's stored overrides.
 * @return {boolean} True when some area overrides a domain.
 */
export function hasAreaOverrides(overrides?: EvaluationCaseOverrides): boolean {
  const areas: unknown = overrides?.areas;
  if (areas === null || typeof areas !== 'object') {
    return false;
  }
  return Object.values(areas).some(
    (domains) =>
      domains !== null &&
      typeof domains === 'object' &&
      Object.keys(domains).length > 0
  );
}

/**
 * The overrides `run_scenarios` is asked about — the form's case-level domain picks and the deck
 * pick, as `price_deck_id`. Nothing area-keyed is sent: a reopened run drops an earlier run's
 * per-area map (`withoutAreaOverrides`).
 *
 * @param {EvaluationCaseOverrides} overrides - The form's overrides.
 * @param {number | null} priceDeckId - The form's deck pick.
 * @return {EvaluationCaseOverrides} The overrides to ask with.
 */
export function runScenarioOverrides(
  overrides: EvaluationCaseOverrides,
  priceDeckId: number | null
): EvaluationCaseOverrides {
  return {
    ...formDomainOverrides(overrides),
    ...(priceDeckId !== null ? { price_deck_id: priceDeckId } : {}),
  };
}

/**
 * Why a case-level run can't start, as the Calculate gate and the note
 * under the active case both say it; null when it can. "Case incomplete: missing … · archived …"
 * from Readiness's domains, else the server's message.
 *
 * @param {RunScenariosResponse} data - The run_scenarios answer.
 * @return {string | null} The reason, or null.
 */
export function runVerdictText(data: RunScenariosResponse): string | null {
  if (data.complete) return null;
  return (
    caseIncompleteText(data.missingDomains, data.archivedDomains) ??
    data.errorMessage ??
    'Case incomplete'
  );
}

/**
 * The form's own case-level picks, `{ domain: id }` — what a case's draft
 * preview sends without waiting on `run_scenarios`. A pick equal to the case's scenario is sent as
 * is: the BE reads it as that scenario either way.
 *
 * @param {EvaluationCaseOverrides} overrides - The form's overrides.
 * @return {object} The shape-carrying domains picked for this run.
 */
export function formDomainOverrides(
  overrides: EvaluationCaseOverrides
): Partial<Record<CaseDomain, number>> {
  return Object.fromEntries(
    SHAPE_CARRYING_DOMAIN_KEYS.filter(
      (domain) => typeof overrides[domain] === 'number'
    ).map((domain) => [domain, overrides[domain] as number])
  );
}

/**
 * A case-level run's overrides on the wire, `{ domain: id }`, from what
 * `run_scenarios` said the run uses. A row back on the case's own scenario goes out as nothing.
 *
 * @param {RunScenarioRow[]} rows - The `run_scenarios` rows.
 * @return {object} The shape-carrying domains this run overrides.
 */
export function caseLevelOverrideWire(
  rows: readonly RunScenarioRow[]
): Partial<Record<CaseDomain, number>> {
  return Object.fromEntries(
    rows
      .filter(
        (row) =>
          row.overridden &&
          row.scenarioId !== null &&
          isShapeCarryingDomainKey(row.domain)
      )
      .map((row) => [row.domain, row.scenarioId as number])
  );
}

// ─── P6 · Output Aggregation (ValueRollup) — request builders + adapters ───
//
// The FR#17 quarantine seam: the request bodies + the wire→domain adapters for the
// three `value_rollup*` endpoints live here (the `adaptMasterDataResponse`
// precedent), so the components/hooks touch only the FE domain shapes. A wire-shape
// change (casing / envelope / renames) is absorbed here, not in the UI.

/**
 * The request fields both P6 data endpoints share (`value_rollup` +
 * `visual_summary`): the calc-mode identity, the group-by dimensions, and the
 * optional cashflow window + page filter/scope. Empty halves are dropped so a
 * "no filter / no scope / no period" request is one stable body (and query key) —
 * the `master_data` payload discipline. `filters` / `mapScope` pass through
 * verbatim (the existing eval3 atoms), no mapping. `calculationId` is always
 * present — P6 is calc-mode only.
 */
interface RollupRequestBaseParams {
  calculationId: number;
  packageId: number;
  dimensions: string[];
  cashflowPeriod?: CashflowPeriod | null;
  filters?: ServerFilter[];
  mapScope?: MapScope;
}

/**
 * The shared request fragment both builders emit — the fields common to
 * `ValueRollupRequest` + `VisualSummaryRequest`. Derived from `ValueRollupRequest`
 * so the wire contract is the single source; the optional halves model the
 * empty-half drop (an absent key), and `cashflowPeriod` is `| undefined` (never
 * `null`) here — the builder's `null` input is erased when the half is dropped.
 */
type RollupRequestBase = Pick<
  ValueRollupRequest,
  | 'calculationId'
  | 'packageId'
  | 'dimensions'
  | 'cashflowPeriod'
  | 'filters'
  | 'mapScope'
>;

/**
 * Builds the common request fields shared by both data endpoints, dropping the
 * empty halves (`cashflowPeriod` when unset, `filters` when empty, `mapScope` when
 * absent).
 *
 * @param {RollupRequestBaseParams} params - The calc identity + dimensions + optional window/filter/scope.
 * @return {RollupRequestBase} The shared request body fragment, empty halves omitted.
 */
function buildRollupRequestBase({
  calculationId,
  packageId,
  dimensions,
  cashflowPeriod,
  filters,
  mapScope,
}: RollupRequestBaseParams): RollupRequestBase {
  return {
    calculationId,
    packageId,
    dimensions,
    ...(cashflowPeriod ? { cashflowPeriod } : {}),
    ...(filters?.length ? { filters } : {}),
    ...(mapScope ? { mapScope } : {}),
  };
}

/**
 * Builds the `POST /queries/packages/value_rollup` request body — the shared base
 * plus offset paging (`page` / `pageSize`) and the optional single-column `sort` +
 * `search`. An empty `sort` and a whitespace-only `search` are dropped (a trimmed
 * search matches the trimmed query key, per the `master_data` discipline).
 *
 * @param {object} params - The base (`RollupRequestBaseParams`) + `page` / `pageSize` + optional `sort` / `search`.
 * @return {ValueRollupRequest} The value_rollup request body.
 */
export function buildValueRollupRequest({
  page,
  pageSize,
  sort,
  search,
  numericBin,
  temporalBin,
  ...base
}: RollupRequestBaseParams & {
  page: number;
  pageSize: number;
  sort?: RollupSort | null;
  search?: string;
  numericBin?: string;
  temporalBin?: string;
}): ValueRollupRequest {
  return {
    ...buildRollupRequestBase(base),
    page,
    pageSize,
    ...(sort ? { sort } : {}),
    ...(search?.trim() ? { search: search.trim() } : {}),
    ...(numericBin ? { numericBin } : {}),
    ...(temporalBin ? { temporalBin } : {}),
  };
}

/**
 * Whether the Value Rollup has something to group by — at least one dimension OR a numeric
 * bin (itself a leading pseudo-dimension). A temporal bin does NOT count: it's an additive
 * side-summary that still needs a dimension. Shared by the fetch gate, the table's
 * blank-state gate, and the content-export config gate so the three can't drift.
 *
 * @param {string[]} dimensions - The effective group-by dimensions.
 * @param {RollupBin | null | undefined} bin - The active bin, or null/undefined.
 * @return {boolean} True when there is a valid grouping.
 */
export function rollupHasGrouping(
  dimensions: string[],
  bin: RollupBin | null | undefined
): boolean {
  return dimensions.length > 0 || bin?.category === 'numeric';
}

/**
 * Derives the two scalar bin request params from the single active bin — mutually exclusive
 * by the atom's single-value shape. Shared by `useValueRollup` + the content export so the
 * derivation can't drift between them.
 *
 * @param {RollupBin | null | undefined} bin - The active bin, or null/undefined.
 * @return {object} The wire bin params `{ numericBin?, temporalBin? }` (empty when no bin).
 */
export function rollupBinParams(bin: RollupBin | null | undefined): {
  numericBin?: string;
  temporalBin?: string;
} {
  if (bin?.category === 'numeric') {
    return { numericBin: bin.presetKey };
  }
  if (bin?.category === 'temporal') {
    return { temporalBin: bin.presetKey };
  }
  return {};
}

/**
 * Builds the `POST /queries/packages/visual_summary` request body — the shared
 * base plus the chart `metric` + top-N `limit`.
 *
 * @param {object} params - The base (`RollupRequestBaseParams`) + the chart `metric` + top-N `limit`.
 * @return {VisualSummaryRequest} The visual_summary request body.
 */
export function buildVisualSummaryRequest({
  metric,
  limit,
  ...base
}: RollupRequestBaseParams & {
  metric: string;
  limit: number;
}): VisualSummaryRequest {
  return {
    ...buildRollupRequestBase(base),
    metric,
    limit,
  };
}

/**
 * Wire→domain adapter for the options catalog (FR#17). Renames the wire
 * `defaults.metrics` (a single key, plural name) to `defaults.metric`; the
 * dimension + metric arrays pass through unchanged (their wire and domain shapes
 * match, so `slotOnly` is preserved). Mirrors `adaptMasterDataResponse`.
 *
 * @param {RollupOptionsResponse} raw - The fetched options catalog (wire shape).
 * @return {RollupOptions} The FE-facing options catalog.
 */
export function adaptRollupOptions(raw: RollupOptionsResponse): RollupOptions {
  return {
    dimensions: raw.dimensions,
    metrics: raw.metrics,
    // Bin presets pass through unchanged (wire + domain shapes match; jbuilder
    // already camelized the keys), feeding the Bin by menu.
    bins: raw.bins,
    defaults: {
      dimensions: raw.defaults.dimensions,
      metric: raw.defaults.metrics,
    },
  };
}

/**
 * Wire→domain adapter for one offset page of the rollup table (FR#17). The BE wraps
 * every row cell as `{ value, href? }` (href only for linkable dimensions — slot /
 * devArea / asset); this unwraps each cell to its scalar `value` for `RollupRow` (so
 * `getRowId` / the total row / cell formatting keep seeing scalars) and collects the
 * hrefs into a per-row `hrefs` map, parallel to `pctOfPackage`. Totals stay flat (the
 * BE doesn't wrap them). The offset envelope passes through unchanged.
 *
 * @param {ValueRollupResponse} raw - The fetched rollup page (wire shape).
 * @return {RollupPage} The FE-facing rollup page.
 */
export function adaptValueRollupPage(raw: ValueRollupResponse): RollupPage {
  return {
    columns: raw.columns,
    rows: raw.rows.map((rawRow) => {
      const row: RollupRow = {};
      // Created lazily on the first linkable cell — most rows carry none, so this
      // avoids minting (then discarding) an empty map per row on a large page.
      let hrefs: Record<string, string> | undefined;
      Object.entries(rawRow).forEach(([key, cell]) => {
        if (cell === undefined) {
          return;
        }
        // `pctOfPackage` is a nested map, not a wrapped cell — carry it through.
        if (key === 'pctOfPackage') {
          row.pctOfPackage = cell as Record<string, number>;
          return;
        }
        const { value, href } = cell as RollupCellResponse;
        row[key] = value;
        if (href) {
          (hrefs ??= {})[key] = href;
        }
      });
      if (hrefs) {
        row.hrefs = hrefs;
      }
      return row;
    }),
    totals: raw.totals,
    cashflowTimeline: raw.cashflowTimeline,
    cashflowPeriodSummary: raw.cashflowPeriodSummary,
    page: raw.page,
    pageSize: raw.pageSize,
    totalCount: raw.totalCount,
    // Temporal-bin summary rides in the same response (detail) and is already the
    // FE-domain shape (self-describing columns + open row records) — pass through.
    ...(raw.temporalBinColumns
      ? { temporalBinColumns: raw.temporalBinColumns }
      : {}),
    ...(raw.temporalBins ? { temporalBins: raw.temporalBins } : {}),
  };
}

// ─── P8 · Content Export (Slice 1) ───

/**
 * A content-export checklist item id. Either a BE data-section key (→ `payload.sections[]`)
 * or an image-group id (→ `charts[key]` PNG parts). The image ids are FE-only labels for the
 * checklist; `charts_visuals` expands to one part per rendered chart at capture time, while
 * `reserves_evolution` is the single fixed `charts[key]` for the Reserves chart.
 */
export type ContentExportItemId =
  | ContentExportSection
  | 'charts_visuals'
  | 'reserves_evolution'
  | 'production_volumes'
  | 'free_cash_flow'
  | 'map'
  | 'spatial_data';

/**
 * One content-export checklist item. An item contributes a data `section` (the `sections[]`
 * key), an `imageGroup` (the {@link ChartExportGroup} whose registered charts feed this item's
 * `charts[key]` PNGs), or BOTH — Reserves Category Evolution exports both its table (a data
 * section) and its chart (an image). At least one of the two is always present.
 */
export interface ContentExportItem {
  id: ContentExportItemId;
  label: string;
  section?: ContentExportSection;
  imageGroup?: ChartExportGroup;
}

/**
 * The content-export checklist, in mock display order — with the mock's display relabels
 * (`slots` → "Properties", `slot_allocations` → "Property Allocations"). Reserves Category
 * Evolution exports BOTH its table (the `reserves_category_evolution` data section, INS-10099)
 * and its chart (`charts[reserves_evolution]`, captured from its always-mounted chart even
 * while the section is hidden — FR9). Passed to the presentational modal so it stays
 * prop-driven + testable.
 */
export const CONTENT_EXPORT_ITEMS: ContentExportItem[] = [
  {
    id: 'evaluation_output_aggregation',
    label: 'Evaluation Output Aggregation',
    // Exports both: the pivoted table (data section) + the Visual Summary chart image
    // (registry group) — the section stays mounted (off-screen when hidden) so the
    // SciChart chart is always capturable.
    section: 'evaluation_output_aggregation',
    imageGroup: 'valueRollup',
  },
  {
    id: 'reserves_evolution',
    label: 'Reserves Category Evolution',
    // Exports both: the pivoted table (data section) + the chart image (registry group).
    section: 'reserves_category_evolution',
    imageGroup: 'reserves',
  },
  {
    // Image-only: a PNG of the Production Volumes chart (Highcharts). Its section stays
    // mounted (CSS-hidden when hidden) so the chart is always capturable (FR9).
    id: 'production_volumes',
    label: 'Production Volumes',
    imageGroup: 'productionVolumes',
  },
  {
    // Image-only: a PNG of the Cash Flow chart (Highcharts). The section's cumulative table
    // has no content-export (no BE data section for aggregated cash flow). Its section stays
    // mounted (CSS-hidden when hidden) so the chart is always capturable.
    id: 'free_cash_flow',
    label: 'Cash Flow',
    imageGroup: 'cashFlow',
  },
  {
    id: 'cashflows_by_month',
    label: 'Cashflows by Month Table',
    section: 'cashflows_by_month',
  },
  {
    id: 'slot_allocations',
    label: 'Property Allocations',
    section: 'slot_allocations',
  },
  { id: 'assets', label: 'Assets', section: 'assets' },
  { id: 'slots', label: 'Properties', section: 'slots' },
  {
    id: 'charts_visuals',
    label: 'Charts & Visuals',
    imageGroup: 'chartsVisuals',
  },
  {
    // Image-only: a PNG of the current Master Data map view (captured WYSIWYG from the
    // live Mapbox canvas). Disabled when the map isn't shown — see `isMapRenderedAtom`.
    id: 'map',
    label: 'Map',
    imageGroup: 'map',
  },
  {
    // Spatial data: geospatial file export (Shapefile, KML, GeoJSON) of the evaluation's
    // Units, Assets, and Slot survey geometries. The spatial config (formats, layers, basis,
    // as_of_date) is sent in config.spatial — see ContentExportPayload.
    id: 'spatial_data',
    label: 'Spatial Data',
    section: 'spatial',
  },
];

/**
 * The default Image Options for the content-export modal — 16:9 at Medium element size, all
 * generic chart elements shown, PNG. Mirrors the single-chart drawer's seed. Returns a fresh
 * object each call (the `elements` map is mutable per-render state).
 *
 * @return {ExportImageOptions} A fresh default options object.
 */
export function defaultContentExportImageOptions(): ExportImageOptions {
  return {
    aspectRatio: '16:9',
    customWidth: 1600,
    customHeight: 900,
    elementSize: 'medium',
    elements: Object.fromEntries(
      GENERIC_CHART_ELEMENTS.map((descriptor) => [
        descriptor.key,
        descriptor.available,
      ])
    ),
    outputFormat: 'png',
  };
}

/**
 * The default Spatial Data export config for the content-export modal — a Shapefile of every layer
 * with all of its attributes, on the eval-run basis (product spec AC 2: all layers, all attributes,
 * Shapefile only on open). Returns a fresh object each call (the `formats` / `layers` arrays and the
 * `attributes` map are mutable per-selection state). Shared by the modal's initial state and its
 * reset-on-open, so the two can't drift.
 *
 * @return {SpatialExportConfig} A fresh default spatial config object.
 */
export function defaultSpatialConfig(): SpatialExportConfig {
  return {
    formats: ['shapefile'],
    layers: SPATIAL_EXPORT_LAYERS.map((layer) => layer.id),
    attributes: Object.fromEntries(
      SPATIAL_EXPORT_LAYERS.map((layer) => [
        layer.id,
        layer.attributes.map((attribute) => attribute.id),
      ])
    ),
    basis: 'eval_run',
  };
}

/**
 * Sanitizes a chart key into a filename-safe `charts[key]` multipart part name (the BE also
 * sanitizes; this keeps the field name clean before it goes on the wire). Any character
 * outside `[A-Za-z0-9._-]` becomes `_`.
 *
 * @param {string} key - The raw chart key (a catalog id, instance id, or fixed reserves key).
 * @return {string} The filename-safe key.
 */
export function sanitizeChartKey(key: string): string {
  return key.replace(/[^A-Za-z0-9._-]/g, '_');
}

/** One captured chart image, bound for a `charts[key]` multipart part. */
export interface ContentExportChartBlob {
  key: string;
  blob: Blob;
}

/**
 * Builds the multipart `FormData` for the content-export POST: the JSON `payload` part plus
 * one `charts[<key>]` File part per captured chart (each named `<key>.png`, keys sanitized).
 * Pure — returns a fresh `FormData`, mutates nothing. An empty `chartBlobs` yields a
 * payload-only body (data sections without any images).
 *
 * @param {ContentExportPayload} payload - The JSON payload (sections + filters + config).
 * @param {ContentExportChartBlob[]} chartBlobs - The captured chart PNGs (may be empty).
 * @return {FormData} The multipart body.
 */
export function buildContentExportFormData(
  payload: ContentExportPayload,
  chartBlobs: ContentExportChartBlob[]
): FormData {
  const formData = new FormData();
  formData.append('payload', JSON.stringify(payload));
  chartBlobs.forEach(({ key, blob }) => {
    const safeKey = sanitizeChartKey(key);
    // The part filename carries the real format so a JPEG capture isn't mislabeled `.png`
    // (the modal offers PNG + JPEG); the BE reads the field-name `key` for the ZIP entry.
    const extension = blob.type === 'image/jpeg' ? 'jpg' : 'png';
    formData.append(`charts[${safeKey}]`, blob, `${safeKey}.${extension}`);
  });
  return formData;
}

/** One selectable column-preset option (id + display label) for the export modal dropdown. */
export interface ColumnPresetOption {
  id: ColumnPreset;
  label: string;
}

/**
 * The master-data column presets offered per Properties / Property Allocations row in the
 * content-export modal (INS-10099) — labels mirror the legacy Eval 2 exports. Array order is
 * dropdown display order only; the opened-on default is {@link DEFAULT_COLUMN_PRESET}. The `id`
 * is sent to the BE as `config.<section>.column_preset`.
 */
export const COLUMN_PRESETS: ColumnPresetOption[] = [
  { id: 'all_columns', label: 'All Columns Format' },
  { id: 'finance_team', label: 'Finance Team Format' },
  { id: 'current_view', label: 'Current View Format' },
];

/**
 * The default column preset the export modal opens on for every preset-bearing table
 * (Properties / Property Allocations / Cashflows by Month) — Finance Team Format, the curated
 * finance subset (product default; the user can switch to All Columns / Current View per table).
 */
export const DEFAULT_COLUMN_PRESET: ColumnPreset = 'finance_team';

/**
 * The content-export sections that carry a per-table column-preset dropdown — the tables the
 * user can pick columns for (Properties / Property Allocations / Cashflows by Month). Assets
 * exports all columns (no dropdown). These match both the `sections[]` keys and the
 * `config.<section>` keys the BE job reads.
 */
export type ColumnPresetSection =
  'slots' | 'slot_allocations' | 'cashflows_by_month';
export const COLUMN_PRESET_SECTIONS: ColumnPresetSection[] = [
  'slots',
  'slot_allocations',
  'cashflows_by_month',
];

/**
 * Extracts the ordered leaf accessor-column keys (react-table column ids) from a DataTable
 * column-def tree — recursing into column groups and skipping display/pseudo columns (the
 * locate pin, selection) that carry no `accessorKey`. These match the table's column ids, so
 * they pick the exportable columns for the "Current View" export preset; run them through
 * {@link toMasterDataExportKeys} before sending, since a few differ from the BE row keys.
 *
 * @param {DataTableColumnDef<T>[]} columns - The column defs handed to a DataTable.
 * @return {string[]} The ordered accessor keys (group + display columns dropped).
 */
export function accessorColumnKeys<T>(
  columns: DataTableColumnDef<T>[]
): string[] {
  return columns.flatMap((column) => {
    if ('columns' in column && Array.isArray(column.columns)) {
      return accessorColumnKeys(column.columns as DataTableColumnDef<T>[]);
    }
    if ('accessorKey' in column && typeof column.accessorKey === 'string') {
      return [column.accessorKey];
    }
    return [];
  });
}

/**
 * FE-accessor → BE-export-row-key renames for the "Current View" preset, per view. Most
 * table columns bind an accessor key that already IS the BE row key, but the "Total Slot
 * NRI/WI" columns are FE domain fields that map to a different BE key: `net*Interest`,
 * the row's own-grain interest, on both the slots and allocations views — matching what
 * `mapTractNriScalars` displays and what `SLOT_FIELD_RENAMES` sorts by, so a Current View
 * export carries the same number the column shows (INS-11417). Any key not listed passes
 * through unchanged; a mapped key absent from the actual rows is dropped by the
 * exporter's intersection.
 */
const MASTER_DATA_EXPORT_KEY_RENAMES: Record<
  MasterDataView,
  Record<string, string>
> = {
  slots: {
    totalSlotNri: 'netRevenueInterest',
    totalSlotWi: 'netWorkingInterest',
  },
  allocations: {
    totalSlotNri: 'netRevenueInterest',
    totalSlotWi: 'netWorkingInterest',
  },
  assets: {},
};

/**
 * Maps a view's FE accessor keys to the BE export row keys the "Current View" export must
 * address (see {@link MASTER_DATA_EXPORT_KEY_RENAMES}). Pick the shown columns FIRST (the
 * table reports column ids, which here are the accessor keys), then map the survivors through
 * this.
 *
 * @param {MasterDataView} view - The master-data view (selects the rename set).
 * @param {string[]} accessorKeys - The visible FE accessor keys.
 * @return {string[]} The corresponding BE export row keys, order preserved.
 */
export function toMasterDataExportKeys(
  view: MasterDataView,
  accessorKeys: string[]
): string[] {
  const renames = MASTER_DATA_EXPORT_KEY_RENAMES[view];
  return accessorKeys.map((key) => renames[key] ?? key);
}

/**
 * Publishes a master-data view's shown columns (as BE export row keys, in on-screen order) to
 * `masterDataVisibleColumnsAtom`, so the content export's "Current View" preset exports the
 * columns the table shows, in the order it shows them — the layout the user saved included.
 * Returns the view's DataTable `onVisibleColumnsChange` handler. Display columns (no
 * `accessorKey`) are dropped. Shared by the Slots + Property Allocations views (identical apart
 * from the view key + its FE→BE key rename); Cashflows publishes its own (a different id
 * vocabulary, no rename).
 *
 * @param {MasterDataView} view - The view these columns belong to (keys the atom + the rename).
 * @param {DataTableColumnDef<T>[]} columns - The view's column defs.
 * @return {Function} The DataTable `onVisibleColumnsChange(columnIds)` handler.
 */
export function useMasterDataVisibleColumns<T>(
  view: MasterDataView,
  columns: DataTableColumnDef<T>[]
): (columnIds: string[]) => void {
  const setVisibleColumns = useSetAtom(masterDataVisibleColumnsAtom);
  const accessorKeys = useMemo(
    () => new Set(accessorColumnKeys(columns)),
    [columns]
  );
  return useCallback(
    (columnIds: string[]) => {
      setVisibleColumns((prev) => ({
        ...prev,
        [view]: toMasterDataExportKeys(
          view,
          columnIds.filter((id) => accessorKeys.has(id))
        ),
      }));
    },
    [view, accessorKeys, setVisibleColumns]
  );
}
